import type { BoxCollider, FloorRegion } from './collision';
import { GRAVITY, JUMP_SPEED, MOVE_SPEED, PLAYER_TOTAL_HEIGHT, PLAYER_RADIUS, VAULT_MAX_HEIGHT } from './controller';

/**
 * The Wave-A gray Astoria block, authored as data per §0.4 ("gray-box
 * geometry is code, not art — iteration on layout must take seconds, not a
 * Codex round-trip"). World scale per ASSET-CONTRACT §1 / PLAN-CITY §3:
 * 1 unit = 1m, sidewalk 2.5m, street lane 3m, block ≈ 60×60m, buildings
 * 12–20m. This file is the ONLY place block dimensions live — block.ts turns
 * it into meshes+colliders, nothing here touches three.js.
 *
 * Layout (top-down, intersection at the origin):
 *   - Two 8m streets cross at the origin, sidewalks (2.5m) line every
 *     building frontage.
 *   - NE quadrant: Building A + Building B split by a 4m alley that runs
 *     north to a small utility yard — the alley IS the connective tissue
 *     linking the street, the fire-escape ramp (mounted on A's alley wall),
 *     and the utility-tunnel entrance (at the yard's north edge).
 *   - SE quadrant: Building D (plain block-massing).
 *   - SW quadrant: Building C (plain block-massing) + a subway stairwell in
 *     the sidewalk margin at the intersection's SW corner (a ramp-approximated
 *     descent per ASSET-CONTRACT §5, dead-ending at a closed-gate landing —
 *     the interior doesn't exist yet, that's S05).
 *   - NW quadrant: left OPEN as the park pocket (benches/planters double as
 *     the vault-test obstacles) instead of a 5th building.
 *   - Building A's roof (16m) is the rooftop parkour goal, reached only via
 *     its fire-escape ramp — walking off any roof edge is intentionally
 *     unguarded (falling off a roof is expected parkour, not a bug).
 */

export interface BuildingSpec {
  id: string;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  height: number;
  color: number;
}

export interface PropSpec {
  id: string;
  x: number;
  z: number;
  width: number;
  depth: number;
  height: number;
  color: number;
}

export interface MarkerSpec {
  id: string;
  x: number;
  y: number;
  z: number;
}

export const STREET_HALF_WIDTH = 4; // 8m-wide streets
export const SIDEWALK_WIDTH = 2.5;
export const SPAWN_POSITION = { x: 0, z: -6 }; // on the sidewalk, facing the intersection

export const BUILDINGS: BuildingSpec[] = [
  { id: 'building_a', minX: 8, maxX: 15, minZ: 8, maxZ: 26, height: 16, color: 0x8a8f9c },
  { id: 'building_b', minX: 19, maxX: 26, minZ: 8, maxZ: 26, height: 14, color: 0x969ba6 },
  { id: 'building_c', minX: -26, maxX: -8, minZ: -26, maxZ: -8, height: 18, color: 0x848a97 },
  { id: 'building_d', minX: 8, maxX: 26, minZ: -26, maxZ: -8, height: 12, color: 0x9a9fa8 },
];

/** Ground-floor door marker on Building A's south (street-facing) wall. S01 shipped it as a closed, collidable panel; S05 makes it interactable (world/interact.ts) — the exterior collider itself stays unchanged (world/interior.ts's own header comment explains why a teleport-based door, not a carved wall gap). */
export const BODEGA_DOOR: PropSpec = {
  id: 'bodega_door',
  x: 11.5,
  z: 8.05,
  width: 2.2,
  depth: 0.15,
  height: 2.4,
  color: 0xff7a2f,
};

/**
 * The one safe standing point just outside the door, on the street side.
 * BODEGA_DOOR sits AT building_a's south wall (z≈8.05, inside the
 * building's own minZ=8 footprint) — a point that only ADDS to door.z
 * would land INSIDE the solid, un-doored wall span. The street/sidewalk
 * side is z<8, so the offset SUBTRACTS. Originally derived inline in S02's
 * respawnPlayer() (main.ts); centralized here in S05 so the three
 * consumers that all need "the safe spot outside the bodega door" —
 * checkpoints.ts's `bodega` respawn point, interact.ts's `bodega_enter`
 * trigger, and main.ts's respawn/exit-teleport call sites — read one
 * number instead of three independently hand-copied `- 1.8`s that could
 * silently drift apart (the exact failure mode Classic gotcha #19 warns
 * about for any formula more than one call site needs).
 */
export const BODEGA_EXTERIOR_STANDPOINT = { x: BODEGA_DOOR.x, z: BODEGA_DOOR.z - 1.8 };

/**
 * T3-1 follow-up 2 (Karan's re-check, 2026-08-26: "that worked but once i
 * leave i can't get back inside"): the walk-out fix taught the player that
 * doors are walkable, so the exterior door must be symmetric — pushing INTO
 * the door face now auto-enters (main.ts runs the same routine the ENTER
 * button uses; the button stays as a redundant path). This predicate is the
 * position half of the trigger; main.ts adds the intent half (the player's
 * DESIRED movement direction must point substantially toward the door —
 * read off PlayerCapsule.lastDesiredDirX/Z, which the collider can't
 * clamp) so neither a pure strafe hugging the storefront wall nor a
 * teleport with no input held can ever fire the walk-in, while a player
 * PINNED against the door face by the collider (zero achieved motion —
 * the review-confirmed dead state a position-delta gate produced) still
 * enters the moment they push at it.
 *
 * The trigger is a real 3D BOX, not a column (review-confirmed HIGH: an
 * unbounded band ran straight across building_a's walkable ROOFTOP —
 * including the rooftop checkpoint at (11.5, 17) — and the open street
 * strip north of the building, teleporting a northbound walker into the
 * bodega from 8-30m away). Derivation, all from the door's own numbers:
 * street face `z - depth/2` = 7.975 (the far bound — no reachable
 * street-level approach exceeds the capsule-stop at 7.675, and the roof
 * deck starts at z=8); the near bound opens DOOR_APPROACH_DEPTH before
 * capsule contact, so a returning player commits ~1.1m past the
 * standpoint (6.25) before anything fires; x is the door's half-width
 * plus a small corner margin, mirroring `isPastDoorwayThreshold`
 * (world/interior.ts); y must be street level (door sill height) — the
 * rooftop above sits at y=16, and a jump in front of the door apexes
 * ~1m, comfortably inside the bound.
 */
const DOOR_APPROACH_DEPTH = 0.3;
const DOOR_FACE_TRIGGER_MIN_Z = BODEGA_DOOR.z - BODEGA_DOOR.depth / 2 - PLAYER_RADIUS - DOOR_APPROACH_DEPTH;
const DOOR_FACE_TRIGGER_MAX_Z = BODEGA_DOOR.z - BODEGA_DOOR.depth / 2;
const DOOR_FACE_TRIGGER_MAX_Y = 2;
export function isAtBodegaDoorFace(x: number, y: number, z: number): boolean {
  return (
    z >= DOOR_FACE_TRIGGER_MIN_Z &&
    z <= DOOR_FACE_TRIGGER_MAX_Z &&
    y < DOOR_FACE_TRIGGER_MAX_Y &&
    Math.abs(x - BODEGA_DOOR.x) <= BODEGA_DOOR.width / 2 + 0.2
  );
}

/**
 * B-2 "Enterable interiors" (PLAN-CITY2 §3) — a second storefront door, on
 * Building B's south wall (same convention as BODEGA_DOOR: minZ=8 for both
 * buildings, so the same z=8.05/depth=0.15 offset lands the panel the same
 * way relative to each building's own wall). x=22.5 is building_b's own
 * center ((19+26)/2). Solid — same "the door panel itself IS the collider,
 * carve nothing out of the building's own south wall" idiom BODEGA_DOOR's
 * own comment establishes (the interior beyond it is teleport-only, per
 * world/interiorLaundromat.ts's header comment).
 */
/**
 * Wave P-4 (PLAN-CITY3 §3): x/width were a hand-copied guess (building_b's
 * OWN center 22.5, not the door itself) — the Greek Market facade
 * (blk_facade_greek_market.glb) ships a real modeled `closed_glass_door`
 * node; measured its own world bbox directly (scratchpad glTF parse, not
 * assumed): x[20.695,21.645] (center 21.170, span 0.950 -> full width 1.9),
 * matching FACADE_PLACEMENT's building_b placement [22.5,0,17]. The gray
 * placeholder panel this spec still drives is removed once that facade
 * loads (block.ts's `attachBuildingFacades`, `this.gray.laundromatDoor`).
 */
export const LAUNDROMAT_DOOR: PropSpec = {
  id: 'laundromat_door',
  x: 21.17,
  z: 8.05,
  width: 1.9,
  depth: 0.15,
  height: 2.4,
  color: 0x4fa8c9,
};

/** Mirrors BODEGA_EXTERIOR_STANDPOINT's exact derivation — z SUBTRACTS since the door sits at the building's own south wall (z<8 is the street/sidewalk side). */
export const LAUNDROMAT_EXTERIOR_STANDPOINT = { x: LAUNDROMAT_DOOR.x, z: LAUNDROMAT_DOOR.z - 1.8 };

/**
 * Mirrors `isAtBodegaDoorFace`'s exact derivation for the second door —
 * reuses the same `DOOR_APPROACH_DEPTH` constant above (a generic "how far
 * before contact does the walk-in trigger open" value, not bodega-specific
 * despite living next to that comment) and the same `y<2` street-level
 * bound. Review finding (LOW, confirmed independently by 3 lenses): an
 * earlier version of this comment justified `y<2` by claiming "building_b
 * has no rooftop region of its own to guard against" — B-1's concurrently-
 * landed `ROOFTOP_B` (below) makes that premise false, but the bound is
 * still safely conservative on its own real merits: `ROOFTOP_B`'s z-range
 * (z>=8) sits entirely outside this trigger's own z-band (7.375-7.975,
 * always <8, i.e. street side only), so a player standing on that roof can
 * never satisfy BOTH conditions — and even considered independently, `y<2`
 * excludes `ROOFTOP_B`'s y=14 by the same wide margin the bodega's own
 * trigger already relies on against building_a's ROOFTOP (y=16).
 */
const LAUNDROMAT_DOOR_FACE_TRIGGER_MIN_Z = LAUNDROMAT_DOOR.z - LAUNDROMAT_DOOR.depth / 2 - PLAYER_RADIUS - DOOR_APPROACH_DEPTH;
const LAUNDROMAT_DOOR_FACE_TRIGGER_MAX_Z = LAUNDROMAT_DOOR.z - LAUNDROMAT_DOOR.depth / 2;
const LAUNDROMAT_DOOR_FACE_TRIGGER_MAX_Y = 2;
export function isAtLaundromatDoorFace(x: number, y: number, z: number): boolean {
  return (
    z >= LAUNDROMAT_DOOR_FACE_TRIGGER_MIN_Z &&
    z <= LAUNDROMAT_DOOR_FACE_TRIGGER_MAX_Z &&
    y < LAUNDROMAT_DOOR_FACE_TRIGGER_MAX_Y &&
    Math.abs(x - LAUNDROMAT_DOOR.x) <= LAUNDROMAT_DOOR.width / 2 + 0.2
  );
}

/** Park-pocket obstacles (NW quadrant) — short enough (0.5m) to be vault targets, not walls. */
export const PARK_PROPS: PropSpec[] = [
  { id: 'planter_1', x: -18, z: 14, width: 1.6, depth: 1.6, height: 0.5, color: 0x5c7a52 },
  { id: 'planter_2', x: -12, z: 20, width: 1.6, depth: 1.6, height: 0.5, color: 0x5c7a52 },
  { id: 'bench_1', x: -20, z: 22, width: 2.2, depth: 0.6, height: 0.45, color: 0x6b5a45 },
  { id: 'bench_2', x: -10, z: 12, width: 2.2, depth: 0.6, height: 0.45, color: 0x6b5a45 },
];

/** Closed subway gate — marks "not open yet" (S05 territory) at the base of the stair descent. */
export const SUBWAY_GATE: PropSpec = {
  id: 'subway_gate',
  x: -5.5,
  z: -17.9,
  width: 3.4,
  depth: 0.2,
  height: 2,
  color: 0x3a3f4a,
};

// --- Alley + fire escape + utility tunnel (NE quadrant connective tissue) ---
export const ALLEY = { minX: 15, maxX: 19, minZ: 8, maxZ: 30 };
export const FIRE_ESCAPE_RAMP = { minX: 15, maxX: 17, minZ: 10, maxZ: 26, y0: 0, y1: 16 };

function rampHeightAt(r: { minZ: number; maxZ: number; y0: number; y1: number }, z: number): number {
  const t = (z - r.minZ) / (r.maxZ - r.minZ);
  return r.y0 + (r.y1 - r.y0) * t;
}

/**
 * T1 3d: the fire-escape ramp's OPEN east edge (x=17, the whole z10-26 run)
 * let sideways drift mid-climb carry the player off the ramp into open air —
 * reproduced (5m fall at y≈5 from any eastward drift past x=17). Railed
 * with stepped wall segments rather than one tall box, because a single box
 * spanning the full 0-16m climb would need bottomY/topY covering the WHOLE
 * range at once, which (per S01's own collision lesson — see
 * STATUS-CITY.md's S01 row: "a wall's vertical bounds must cover the FULL
 * range motion can traverse through it, not a single resting height") is
 * exactly the class of bug already found and fixed three separate times in
 * this block. Each 2m-of-z segment's own bottomY/topY only needs to cover
 * the height range traversed ACROSS THAT SEGMENT, plus margin — the ramp's
 * 45° slope is rise=run (16m of height over its own 16m z-run), so a 2m
 * segment traverses a full 2m of height, not half of it; RAIL_BOTTOM_MARGIN/
 * RAIL_TOP_MARGIN below are sized against that 2m, not 1m. Exported (not
 * just built for `allWalls()`) so block.ts's visual handrail boxes are built
 * from these SAME numbers, matching the ramp mesh's own "one set of
 * numbers, not two" discipline (see `addRampMesh`'s comment in block.ts).
 */
const RAIL_SEGMENT_LENGTH = 2; // meters of z per stepped segment — 16m run / 2m = 8 segments, no remainder
const RAIL_THICKNESS = 0.4;
const RAIL_BOTTOM_MARGIN = 0.6;
// Review fix: a fixed 1.2 left a segment's far-end clearance (topY minus
// feet, which bottoms out at exactly RAIL_TOP_MARGIN at each segment's own
// far z) below the jump apex — a jump timed at the worn end of any segment
// cleared the rail and fell through, into the same class of hole this rail
// exists to close. DERIVED from controller.ts's real exported constants
// (apex = v0²/2g = 1.40625m continuous, ~1.34m measured discretely at
// 60fps) plus a 0.25m margin, rather than hand-copying the apex value —
// so a future tuning pass touching JUMP_SPEED/GRAVITY moves this rail
// automatically instead of silently re-opening the gap (T1 review finding).
const JUMP_APEX = (JUMP_SPEED * JUMP_SPEED) / (2 * Math.abs(GRAVITY));
const RAIL_TOP_MARGIN = JUMP_APEX + 0.25;
/**
 * The REAL discretely-achieved jump apex at the game's fixed 60Hz timestep —
 * this comment block's own line above already notes the gap between the
 * continuous v0²/2g formula (JUMP_APEX, 1.40625m) and what a real 60fps
 * simulation actually reaches (~1.34m), but that discrete number had only
 * ever been hand-typed into a comment, never computed. Simulated here via
 * the SAME semi-implicit-Euler integration `PlayerCapsule.update()` itself
 * uses (`velocityY += GRAVITY*dt; y += velocityY*dt`) at the real 60Hz
 * `dt` — B-1 review finding (PLAN-CITY2 §3): any future reachability claim
 * that compares a rise/height against "the jump apex" should use THIS, not
 * the continuous `JUMP_APEX`, which overstates real clearance by ~5%.
 */
function discreteJumpApex(): number {
  const dt = 1 / 60;
  let v = JUMP_SPEED;
  let y = 0;
  let peak = 0;
  for (let i = 0; i < 600; i++) {
    v += GRAVITY * dt;
    y += v * dt;
    if (y > peak) peak = y;
    if (y <= 0) break;
  }
  return peak;
}
export const DISCRETE_JUMP_APEX = discreteJumpApex();
// A wall is inactive against a PLAYER_TOTAL_HEIGHT (1.4m) capsule standing
// at street level (feetY=0) once its own bottomY >= 1.4 (bottomY >= headY,
// collision.ts's own vertical-clearance skip). Every segment from z=12 on
// already clears this on its own (rampHeightAt(12) - 0.6 = 1.4) — floored in
// explicitly below so segment 0 (z10-12, whose raw bottomY would be
// rampHeightAt(10) - 0.6 = -0.6) doesn't stay the one exception. Left at
// -0.6, review found it becomes a full-height wall AT STREET LEVEL across
// x[17,17.4] z[10,12] — invisible to nobody (block.ts renders it), but
// confirmed to pin a chasing melee enemy against it indefinitely (enemies
// run the same BlockPhysics with no pathfinding yet, ai/enemy.ts's own
// S02-scope note), closing off a permanent safe pocket just east of it.
// Flooring bottomY here makes segment 0 inert at true street level exactly
// like every other segment, while it's still fully active the instant a
// climbing player's feet leave the ground (feetY>0 puts headY>1.4, clearing
// the floor) — the drift guard this rail exists for is unaffected. Tied to
// the real capsule height (not a hand-copied 1.4) for the same
// drift-protection reason as JUMP_APEX above — with one caveat that
// derivation can't fully cover: ENEMIES resolve walls against their own
// vertical band, imported from this SAME `PLAYER_TOTAL_HEIGHT` constant at
// three sites (S12 gate-pass fix-round; all three used to be hand-copied
// `1.4` literals that merely HAPPENED to match) — ai/enemy.ts's two
// `resolveHorizontal` calls, AND navgrid.ts's own `AGENT_CLEARANCE_HEIGHT`
// (the third copy was missed on the first pass of this fix and only caught
// by a don't-break-list review grep — a reminder that "grep every OTHER
// hardcoded copy of a value being converted to a shared constant" needs to
// mean the WHOLE codebase, not just the file(s) already known to have one).
// The three can no longer numerically drift apart from EACH OTHER, but
// they're still logically independent uses of one number — if the PLAYER's
// height is ever intentionally raised without a matching change to enemy
// hitboxes, this floor rises with it and segment 0 re-arms as a
// street-level wall for the (now-shorter-relative) enemies (T1 review's
// original finding).
const STREET_INERT_BOTTOM_Y = PLAYER_TOTAL_HEIGHT;

export const FIRE_ESCAPE_RAIL_SEGMENTS: BoxCollider[] = (() => {
  const segments: BoxCollider[] = [];
  const r = FIRE_ESCAPE_RAMP;
  const segCount = Math.round((r.maxZ - r.minZ) / RAIL_SEGMENT_LENGTH);
  for (let i = 0; i < segCount; i++) {
    const zStart = r.minZ + i * RAIL_SEGMENT_LENGTH;
    const zEnd = zStart + RAIL_SEGMENT_LENGTH;
    segments.push({
      minX: r.maxX,
      maxX: r.maxX + RAIL_THICKNESS,
      minZ: zStart,
      maxZ: zEnd,
      // Near the ramp's own bottom (z≈10-12) the RAW rampHeightAt-derived
      // bottomY would dip below street level and block street-level
      // crossing at that XZ — accepted per the T1 brief for the crossing
      // itself, but the Math.max floor stops that same dip from ALSO making
      // this segment a permanent street-level wall (see
      // STREET_INERT_BOTTOM_Y's own comment).
      bottomY: Math.max(rampHeightAt(r, zStart) - RAIL_BOTTOM_MARGIN, STREET_INERT_BOTTOM_Y),
      topY: rampHeightAt(r, zEnd) + RAIL_TOP_MARGIN,
    });
  }
  return segments;
})();
// Building A's own roof footprint — deliberately does NOT widen to match
// the ramp's x15-17 across its full Z run: a first attempt at that (one
// region, x8-17 for the whole z8-26 span) made the flat roof height (16)
// directly overlap the ramp's own still-climbing footprint at every point
// along it, and "highest priority, then highest height" always picked the
// flat 16 the instant the player stepped onto the ramp anywhere — not just
// at the top — destroying the gradual climb entirely (caught by a manual
// frame trace: y was already 16 at the very first tick). maxX is exactly
// 15 (the ramp's own minX), not 15.5 — an earlier version used 15.5 "for a
// slight overhang" and left a real, if narrow, x[15,15.5] sliver where this
// region and FIRE_ESCAPE_RAMP both cover the same XZ (their z-ranges both
// span into the same area, unlike ROOFTOP_LANDING below, which shares only
// the z=26 edge with the ramp) — close enough to the building's own east
// wall that resolveHorizontal keeps a climbing player pinned to ~x15.3, but
// a 3-lens review confirmed it still produced an early height-snap in the
// last ~0.2m of the climb, and the dev-tap teleport (which uses a much more
// generous tolerance, see teleport()'s own comment) could land on rooftop
// height from a tap that visually looked like it hit the ramp. A shared
// EDGE at x=15 (this region owns nothing past it) removes the sliver
// entirely, mirroring the ROOFTOP/ROOFTOP_LANDING pattern already used at
// z=26 — no more "slight overhang," but zero overlap is worth more than a
// half-meter of unused visual margin.
export const ROOFTOP = { minX: 8, maxX: 15, minZ: 8, maxZ: 26, y: 16 };
// A small landing at the ramp's own top-end width (15-17), starting exactly
// where the ramp's own z-range ends (26) — this is what actually fixes
// "walk straight off the ramp's edge and fall": it catches a player who
// never strafed onto the narrower main roof deck, without ever competing
// against the ramp's climbing height (their z-ranges don't overlap at all).
export const ROOFTOP_LANDING = { minX: 15, maxX: 17, minZ: 26, maxZ: 30, y: 16 };

/**
 * Review fix (not in the original T1 brief): FIRE_ESCAPE_RAIL_SEGMENTS above
 * only covers the ramp's own z10-26 run. The rail does its job — it pins a
 * drifting climber at exactly x=16.7 (PLAYER_RADIUS inset from x=17) for the
 * whole climb — which means it DELIVERS that same player onto
 * ROOFTOP_LANDING already 0.3m from ITS OWN unguarded east edge (x=17),
 * still holding east input, with nothing guarding x=17 for z=26-30.
 * Reproduced: the exact held-input that used to cost a ~5m mid-climb fall
 * instead carried the player onto the landing and off its east side into a
 * 16m street-level fall — the rail's own goal (drift can't silently drop the
 * player) deferred rather than met, at a steeper cost than before. This one
 * extra collider closes it. ROOFTOP_LANDING is FLAT (y=16 throughout, no
 * rampHeightAt), so bottomY/topY are fixed rather than per-segment: bottomY
 * continues the last ramp segment's own bottom margin (rampHeightAt(26) −
 * RAIL_BOTTOM_MARGIN), topY uses the same RAIL_TOP_MARGIN every ramp segment
 * gets (clears the jump apex the same way). Only THIS edge closes — the
 * landing's north edge (z=30) and the main ROOFTOP deck to the west stay
 * intentionally unguarded, per this file's own "falling off a roof is legal
 * parkour" design (see ROOFTOP's comment above). block.ts's
 * `addFireEscapeRail()` renders this alongside the ramp segments so the
 * visual rail and this collider can never disagree.
 */
export const FIRE_ESCAPE_LANDING_RAIL: BoxCollider = {
  minX: FIRE_ESCAPE_RAMP.maxX,
  maxX: FIRE_ESCAPE_RAMP.maxX + RAIL_THICKNESS,
  minZ: ROOFTOP_LANDING.minZ,
  maxZ: ROOFTOP_LANDING.maxZ,
  bottomY: rampHeightAt(FIRE_ESCAPE_RAMP, FIRE_ESCAPE_RAMP.maxZ) - RAIL_BOTTOM_MARGIN,
  topY: FIRE_ESCAPE_RAMP.y1 + RAIL_TOP_MARGIN,
};

// ============================================================================
// B-1 "Rooftop network" (PLAN-CITY2 §3): a second and third reachable roof,
// linked to Building A's existing ROOFTOP — one by a genuine skill JUMP
// across the alley, one by a genuine skill CLIMB up a second ramp — plus a
// small raised vantage platform (the "at least one roof-only pickup/vantage"
// requirement) reached by a third genuine jump. All three traversal checks
// below are DERIVED from JUMP_SPEED/GRAVITY/MOVE_SPEED/VAULT_MAX_HEIGHT, per
// this file's own established discipline (see JUMP_APEX/RAIL_TOP_MARGIN
// above) — never eyeballed. Falling short of any jump, or walking off either
// new roof's own unguarded edges, drops the player back to street level —
// the same "falling off a roof is legal parkour" law ROOFTOP's own header
// comment already establishes; no return ramp is built for ROOFTOP_B.
//
// A live probe of the shipped C04 art (2026-08-26, this session) found that
// EVERY building's facade GLB already ships its own named `roof_deck` +
// `parapet_n/s/e/w` nodes — `attachBuildingFacades()` (block.ts) only ever
// wired building_a's up. Measured world-space bounding boxes confirm each
// one sits exactly at that building's own `height` (building_b's roof_deck
// top face: y=14.00 across x[19,26] z[8,26]; building_d's: y=12.00 across
// x[8,26] z[-26,-8] — both match BUILDINGS' own recorded height/footprint to
// the millimeter), so ROOFTOP_B/ROOFTOP_D below are real, already-modeled
// surfaces this section merely makes walkable — no new gray-box deck mesh
// needed for either (block.ts's own B-1 edit just registers each real
// `roof_deck` node into `groundMeshes`, mirroring building_a's exact idiom).

/** Building B's own roof deck — its full footprint (nothing else in this file shares Building B's XZ footprint the way FIRE_ESCAPE_RAMP shares part of Building A's, so no edge trim is needed here). */
export const ROOFTOP_B = { minX: 19, maxX: 26, minZ: 8, maxZ: 26, y: 14 };

/**
 * The alley gap (ALLEY.minX to ALLEY.maxX, 4m) is the ONLY way onto
 * ROOFTOP_B — jump east off ROOFTOP's own unguarded edge (x=15) and land on
 * ROOFTOP_B's west edge (x=19), a 2m DROP (16→14) across a 4m gap. Neither
 * building's east/west wall needs a cutout for this: `collision.ts`'s own
 * vertical-clearance rule (`wall.topY<=feetY` → inactive) already makes
 * building_a's east wall (topY=16) inert to a player standing AT y=16, and
 * building_b's west wall (topY=14) inert to a player landing AT y=14 — the
 * exact same rule that already lets a player standing on ROOFTOP approach
 * its own east edge in the first place (confirmed by reading
 * `resolveHorizontal` directly, not assumed).
 *
 * Derivation (never eyeballed, per PLAN-CITY2 §3's own instruction): solving
 * the real jump arc `0.5*GRAVITY*t² + JUMP_SPEED*t = dy` for its one valid
 * positive root gives the total hang time of a jump that DROPS `dy` below
 * its launch height (`jumpHangTimeForDrop` below); holding full MOVE_SPEED
 * the entire arc, the max horizontal distance reachable is
 * `MOVE_SPEED * hangTime`. For this 2m drop that's ≈4.60m — the real 4m
 * alley gap leaves ≈0.60m/13% of margin (comparable to RAIL_TOP_MARGIN's own
 * +0.25m/18% margin choice above): enough to clear a jump timed at the true
 * edge without demanding frame-perfect input, while still requiring a
 * genuine committed jump (a player who merely walks off ROOFTOP's edge with
 * no jump falls straight into the alley, per the same physics).
 */
function jumpHangTimeForDrop(dropHeight: number): number {
  return (-JUMP_SPEED - Math.sqrt(JUMP_SPEED * JUMP_SPEED - 2 * GRAVITY * dropHeight)) / GRAVITY;
}
const ROOFTOP_GAP_DROP = ROOFTOP.y - ROOFTOP_B.y; // 2m
/** Exported so the harness can assert this gap is genuinely reachable, the same "expose the raw derived value for direct testing" convention this project already uses elsewhere. */
export const ROOFTOP_GAP_MAX_REACH = MOVE_SPEED * jumpHangTimeForDrop(ROOFTOP_GAP_DROP);
export const ROOFTOP_GAP_ACTUAL = ALLEY.maxX - ALLEY.minX; // 4m — the real gap between ROOFTOP's east edge and ROOFTOP_B's west edge

/**
 * A second ramp up to Building D's own roof (y=12), mirroring
 * FIRE_ESCAPE_RAMP's exact construction: touches D's own east wall on its
 * MIN-X side (already-solid, needs no rail — same "one side borders real
 * building wall" shape FIRE_ESCAPE_RAMP has against building_a's east wall)
 * and needs a drift-guard rail on its OPEN east side (x=28). 12m run for a
 * 12m rise keeps the SAME 45° slope FIRE_ESCAPE_RAMP already established as
 * this project's one walkable-ramp convention. Placed on the open street
 * east of Building D (x∈[26,28], clear of every `ai/habitats.ts` zone, every
 * `STREET_PROP_COLLIDERS`/`SCAFFOLD_COLLIDERS` box, and `CORONA_EXIT_BARRIER`
 * — checked directly against each, not assumed) — the D roof gives a real
 * vantage over the construction pocket/Gridlock lair just south of it,
 * seeding Karan's own "shoot down from a rooftop" idea with a second,
 * distinct roof.
 */
export const ROOFTOP_D_RAMP = { minX: 26, maxX: 28, minZ: -24, maxZ: -12, y0: 0, y1: 12 };
export const ROOFTOP_D = { minX: 8, maxX: 26, minZ: -26, maxZ: -8, y: 12 };

const ROOFTOP_D_RAIL_SEGMENTS_INTERNAL: BoxCollider[] = (() => {
  const segments: BoxCollider[] = [];
  const r = ROOFTOP_D_RAMP;
  const segCount = Math.round((r.maxZ - r.minZ) / RAIL_SEGMENT_LENGTH);
  for (let i = 0; i < segCount; i++) {
    const zStart = r.minZ + i * RAIL_SEGMENT_LENGTH;
    const zEnd = zStart + RAIL_SEGMENT_LENGTH;
    segments.push({
      minX: r.maxX,
      maxX: r.maxX + RAIL_THICKNESS,
      minZ: zStart,
      maxZ: zEnd,
      // Same STREET_INERT_BOTTOM_Y floor as FIRE_ESCAPE_RAIL_SEGMENTS's own
      // bottom segment — this ramp's own z=-24 start is ALSO street level
      // (y0=0), so its raw bottomY would dip the identical -0.6 below true
      // street height and become a permanent street-level wall without it.
      bottomY: Math.max(rampHeightAt(r, zStart) - RAIL_BOTTOM_MARGIN, STREET_INERT_BOTTOM_Y),
      topY: rampHeightAt(r, zEnd) + RAIL_TOP_MARGIN,
    });
  }
  return segments;
})();
export const ROOFTOP_D_RAIL_SEGMENTS = ROOFTOP_D_RAIL_SEGMENTS_INTERNAL;

/**
 * T1's own FIRE_ESCAPE_LANDING_RAIL fix, re-derived for this second ramp: the
 * rail above pins a drifting climber at x≈27.7 (RAIL_THICKNESS-inset from
 * x=28) for the whole climb, which does NOT overlap ROOFTOP_D's own x≤26
 * footprint — a player who rode the rail to the top (z=-12) still holding
 * east+north input would walk straight off the ramp's own end into open air
 * (nothing exists at x≈28, z>-12) instead of reaching the roof, reproducing
 * the exact bug class T1's review caught once already. Fixed the same way:
 * a flat landing patch at the ramp's own width (x[26,28]) extending forward
 * to the roof's own north edge (z=-8, ROOFTOP_D's own maxZ) — walking WEST
 * from anywhere on it reaches the real roof at the same height (wall
 * inactive at y=12, the same rule the alley jump above relies on) — plus a
 * matching rail extension across ITS open east edge. The landing's own far
 * (north) edge is left intentionally unguarded, mirroring ROOFTOP_LANDING's
 * own north edge — falling off a roof/landing's outer edge is legal parkour.
 */
export const ROOFTOP_D_LANDING = { minX: ROOFTOP_D_RAMP.maxX - 2, maxX: ROOFTOP_D_RAMP.maxX, minZ: ROOFTOP_D_RAMP.maxZ, maxZ: ROOFTOP_D.maxZ, y: ROOFTOP_D_RAMP.y1 };
export const ROOFTOP_D_LANDING_RAIL: BoxCollider = {
  minX: ROOFTOP_D_RAMP.maxX,
  maxX: ROOFTOP_D_RAMP.maxX + RAIL_THICKNESS,
  minZ: ROOFTOP_D_LANDING.minZ,
  maxZ: ROOFTOP_D_LANDING.maxZ,
  bottomY: rampHeightAt(ROOFTOP_D_RAMP, ROOFTOP_D_RAMP.maxZ) - RAIL_BOTTOM_MARGIN,
  topY: ROOFTOP_D_RAMP.y1 + RAIL_TOP_MARGIN,
};

/**
 * The roof-only vantage/pickup (PLAN-CITY2 §3's "at least one"), on
 * ROOFTOP_D overlooking the construction pocket/Gridlock lair just south of
 * it — a small raised platform requiring its OWN genuine jump, derived (not
 * eyeballed) from the SAME two constants the file already exports for
 * exactly this purpose: `rise` must exceed VAULT_MAX_HEIGHT (1.1m) so
 * reaching it needs a real jump, not `findVaultLedge`'s automatic climb-
 * assist (collision.ts refuses any ledge above `maxVaultHeight`), and must
 * stay below the REAL discretely-achieved apex with margin so a standing
 * jump can clear it. Review finding (spec-fidelity, CONFIRMED): the first
 * cut compared `rise` against the continuous `JUMP_APEX` (~1.406m) and
 * claimed "~0.21m/15% clear" — but this file's OWN RAIL_TOP_MARGIN comment
 * a few lines up already establishes that the continuous formula overstates
 * real clearance (measured ~1.34m discretely); using `DISCRETE_JUMP_APEX`
 * (computed just above, not hand-typed) the tower's TRUE margin is
 * ~0.144m/10.7% — thinner than first claimed, but still real margin, and a
 * genuine `ROOFTOP_VANTAGE_CLEARANCE_MARGIN` constant below lets the harness
 * assert it stays positive instead of trusting a comment. 1.2m sits 0.1m
 * clear of VAULT_MAX_HEIGHT on the low side. Footprint (2.4×2.4m) sits well
 * inside ROOFTOP_D's own bounds with real margin to every edge and to
 * ROOFTOP_D_LANDING. Review finding (CONFIRMED): the matching interact
 * trigger (world/interact.ts) originally used a radius (1.0m) SMALLER than
 * this platform's own half-width (1.2m), leaving a dead zone near its
 * edges/corners (up to 1.697m from center) where a legitimately-standing
 * player got no prompt — interact.ts now widens the radius to cover the
 * whole top surface and instead excludes ground-level access via a real
 * grounded+altitude check in main.ts's own 'rooftop_vantage' handler (a
 * second review finding: `InteractSystem` has no Y-check at all, so the
 * XZ-only radius could never have distinguished "standing on top" from "the
 * player's XZ merely passed overhead mid-jump" — see that handler's own
 * comment).
 */
const ROOFTOP_VANTAGE_HALF = 1.2;
const ROOFTOP_VANTAGE_RISE = 1.2;
export const ROOFTOP_VANTAGE_TOWER = { x: 17, z: -24, halfWidth: ROOFTOP_VANTAGE_HALF, rise: ROOFTOP_VANTAGE_RISE };
/** Exported so the harness (and main.ts's own altitude gate) can assert this stays positive instead of trusting the comment above — see DISCRETE_JUMP_APEX's own header for why this is the correct apex to compare against, not the continuous JUMP_APEX. */
export const ROOFTOP_VANTAGE_CLEARANCE_MARGIN = DISCRETE_JUMP_APEX - ROOFTOP_VANTAGE_RISE;
export const ROOFTOP_VANTAGE_TOWER_COLLIDER: BoxCollider = {
  minX: ROOFTOP_VANTAGE_TOWER.x - ROOFTOP_VANTAGE_HALF,
  maxX: ROOFTOP_VANTAGE_TOWER.x + ROOFTOP_VANTAGE_HALF,
  minZ: ROOFTOP_VANTAGE_TOWER.z - ROOFTOP_VANTAGE_HALF,
  maxZ: ROOFTOP_VANTAGE_TOWER.z + ROOFTOP_VANTAGE_HALF,
  bottomY: ROOFTOP_D.y,
  topY: ROOFTOP_D.y + ROOFTOP_VANTAGE_RISE,
};
const ROOFTOP_VANTAGE_TOWER_TOP: FloorRegion = {
  kind: 'flat',
  minX: ROOFTOP_VANTAGE_TOWER_COLLIDER.minX,
  maxX: ROOFTOP_VANTAGE_TOWER_COLLIDER.maxX,
  minZ: ROOFTOP_VANTAGE_TOWER_COLLIDER.minZ,
  maxZ: ROOFTOP_VANTAGE_TOWER_COLLIDER.maxZ,
  y: ROOFTOP_VANTAGE_TOWER_COLLIDER.topY,
};
/** The interact-trigger position (world/interact.ts) and dev-teleport marker for the vantage platform's own top surface. */
export const ROOFTOP_VANTAGE_MARKER = { x: ROOFTOP_VANTAGE_TOWER.x, y: ROOFTOP_VANTAGE_TOWER_COLLIDER.topY, z: ROOFTOP_VANTAGE_TOWER.z };
/** Exported so the harness can assert the tower genuinely requires a jump (exceeds auto-vault range) rather than re-deriving the same comparison. */
export const ROOFTOP_VANTAGE_REQUIRES_JUMP = ROOFTOP_VANTAGE_TOWER.rise > VAULT_MAX_HEIGHT;
// ============================================================================

export const TUNNEL_RAMP = { minX: 14, maxX: 20, minZ: 26, maxZ: 29, y0: 0, y1: -3 };
export const TUNNEL_FLOOR = { minX: 14, maxX: 20, minZ: 29, maxZ: 40, y: -3 };
// Low ceiling, purely as the SIDE WALLS' height (visual + horizontal-collision
// extent) — deliberately NOT an overhead collider. `collision.ts` only
// resolves horizontal (XZ) pushback; a ceiling needs vertical (Y) capping,
// which this pass doesn't implement. A full jump's apex (~1.41m rise) would
// exceed this clearance (2.6m ceiling − 1.4m capsule height = 1.2m headroom)
// if a ceiling collider existed, so adding one without also capping jump
// velocity would produce a worse bug (a phantom horizontal shove mid-jump)
// than the omission does (jumping "through" a ceiling that isn't rendered
// either, so there's nothing to visibly clip). Revisit if a future session
// adds vertical collision.
export const TUNNEL_HEIGHT = 2.6;

// --- Subway stair (SW corner of the intersection sidewalk margin) ---
// y0 (at minZ=-14, the south end) is the DEEP end connecting to the landing
// at -3.5; y1 (at maxZ=-6, the north end, facing the intersection) is
// street level. An earlier version had these swapped — height 0 at the
// deep end and -3.5 at the street-facing entrance, i.e. backwards, caught
// by manually tracing a walk down the stair and finding the player free-
// falling into the void once they crossed back out the (wrong) shallow end.
export const SUBWAY_STAIR = { minX: -8, maxX: -3, minZ: -14, maxZ: -6, y0: -3.5, y1: 0 };
export const SUBWAY_LANDING = { minX: -8, maxX: -3, minZ: -18, maxZ: -14, y: -3.5 };

// priority 0 — the map's default/fallback surface. Every other region
// defaults to priority 1 (collision.ts's DEFAULT_PRIORITY), so a ramp or
// stair overlapping this blanket plane always wins once it qualifies by
// height, instead of the ubiquitous street plane winning by having a
// higher raw height at every point along a descent (see collision.ts).
export const STREET_FLOOR: FloorRegion = { kind: 'flat', minX: -40, maxX: 40, minZ: -40, maxZ: 40, y: 0, priority: 0 };

/**
 * Every vaultable prop's TOP surface must also be a real floor region, not
 * just a wall for horizontal collision — without this, vaulting onto a
 * planter deposits the player at the right height but onto nothing gravity
 * recognizes as ground, so the very next tick they fall straight back
 * through the prop's own collision volume and get shoved back off by
 * `resolveHorizontal`, which (with nothing to stand on) immediately
 * re-triggers the vault — an infinite vault/fall oscillation, confirmed via
 * a manual frame-by-frame trace. `groundHeightAt`'s tolerance also had to
 * shrink (STEP_TOLERANCE 0.5 → 0.2, controller.ts) so a 0.5m-tall prop's
 * new floor region doesn't sit exactly AT the old tolerance boundary and
 * get selected while merely walking past it at street level, well before
 * any vault happens.
 */
const PARK_PROP_TOPS: FloorRegion[] = PARK_PROPS.map((p) => ({
  kind: 'flat' as const,
  minX: p.x - p.width / 2,
  maxX: p.x + p.width / 2,
  minZ: p.z - p.depth / 2,
  maxZ: p.z + p.depth / 2,
  y: p.height,
}));

export const FLOORS: FloorRegion[] = [
  STREET_FLOOR,
  { kind: 'ramp', ...FIRE_ESCAPE_RAMP, axis: 'z' },
  { kind: 'flat', ...ROOFTOP },
  { kind: 'flat', ...ROOFTOP_LANDING },
  // B-1 rooftop network — see that section's own comments above.
  { kind: 'flat', ...ROOFTOP_B },
  { kind: 'ramp', ...ROOFTOP_D_RAMP, axis: 'z' },
  { kind: 'flat', ...ROOFTOP_D },
  { kind: 'flat', ...ROOFTOP_D_LANDING },
  ROOFTOP_VANTAGE_TOWER_TOP,
  { kind: 'ramp', ...TUNNEL_RAMP, axis: 'z' },
  { kind: 'flat', ...TUNNEL_FLOOR },
  { kind: 'ramp', ...SUBWAY_STAIR, axis: 'z' },
  { kind: 'flat', ...SUBWAY_LANDING },
  ...PARK_PROP_TOPS,
];

/** Building exteriors as 4 wall segments each (not 1 solid box) so doorway/alley gaps are just the segment that's omitted. */
export function buildingWalls(b: BuildingSpec): BoxCollider[] {
  const t = 0.4; // wall thickness
  const walls: BoxCollider[] = [
    { minX: b.minX, maxX: b.maxX, minZ: b.minZ, maxZ: b.minZ + t, topY: b.height, bottomY: 0 }, // south
    { minX: b.minX, maxX: b.maxX, minZ: b.maxZ - t, maxZ: b.maxZ, topY: b.height, bottomY: 0 }, // north
    { minX: b.minX, maxX: b.minX + t, minZ: b.minZ, maxZ: b.maxZ, topY: b.height, bottomY: 0 }, // west
    { minX: b.maxX - t, maxX: b.maxX, minZ: b.minZ, maxZ: b.maxZ, topY: b.height, bottomY: 0 }, // east
  ];
  return walls;
}

export function allWalls(): BoxCollider[] {
  const walls: BoxCollider[] = [];
  for (const b of BUILDINGS) walls.push(...buildingWalls(b));

  // Bodega door: solid for S01 (no interior yet) — carve nothing out of
  // building_a's south wall; the door panel itself IS the collider there.
  walls.push({
    minX: BODEGA_DOOR.x - BODEGA_DOOR.width / 2,
    maxX: BODEGA_DOOR.x + BODEGA_DOOR.width / 2,
    minZ: BODEGA_DOOR.z - BODEGA_DOOR.depth / 2,
    maxZ: BODEGA_DOOR.z + BODEGA_DOOR.depth / 2,
    topY: BODEGA_DOOR.height,
    bottomY: 0,
  });

  // B-2: laundromat door — same solid-panel idiom as BODEGA_DOOR above.
  walls.push({
    minX: LAUNDROMAT_DOOR.x - LAUNDROMAT_DOOR.width / 2,
    maxX: LAUNDROMAT_DOOR.x + LAUNDROMAT_DOOR.width / 2,
    minZ: LAUNDROMAT_DOOR.z - LAUNDROMAT_DOOR.depth / 2,
    maxZ: LAUNDROMAT_DOOR.z + LAUNDROMAT_DOOR.depth / 2,
    topY: LAUNDROMAT_DOOR.height,
    bottomY: 0,
  });

  // Park props are low enough to be vault ledges, not walls to bump into —
  // they're still registered as walls (findVaultLedge reads the same list)
  // but their short height keeps resolveHorizontal's headY check irrelevant
  // once vaulted; below the vault threshold they DO block normal walking,
  // which is correct (you vault OVER a bench, you don't walk through it).
  for (const p of PARK_PROPS) {
    walls.push({
      minX: p.x - p.width / 2,
      maxX: p.x + p.width / 2,
      minZ: p.z - p.depth / 2,
      maxZ: p.z + p.depth / 2,
      topY: p.height,
      bottomY: 0,
    });
  }

  walls.push({
    minX: SUBWAY_GATE.x - SUBWAY_GATE.width / 2,
    maxX: SUBWAY_GATE.x + SUBWAY_GATE.width / 2,
    minZ: SUBWAY_GATE.z - SUBWAY_GATE.depth / 2,
    maxZ: SUBWAY_GATE.z + SUBWAY_GATE.depth / 2,
    topY: SUBWAY_LANDING.y + SUBWAY_GATE.height,
    bottomY: SUBWAY_LANDING.y,
  });

  // S10: the Corona exit gate — a real, FULL-WIDTH collider (see
  // CORONA_EXIT_BARRIER's own comment for why it's wider than the visual
  // gate prop itself), not just a visual panel. x=34 sits outside NavGrid's
  // own BOUNDS (x −30..30, world/navgrid.ts) — same "no ground enemy can
  // ever reach it" property boundaryWalls()'s own comment already
  // establishes for the play boundary, so this can't interact with
  // pathing/spawning either.
  walls.push(CORONA_EXIT_BARRIER);

  // Subway side walls — same fix, same reason as the tunnel's below: the
  // stair (SUBWAY_STAIR) and landing (SUBWAY_LANDING) previously had no
  // side collision at all, so drifting sideways off either one while below
  // street level free-fell into the void with nothing to catch the player
  // until the -20 safety net teleported them back to spawn. topY is 1.5 —
  // comfortably ABOVE street level (0), not pinned to it — because the
  // stair's own height continuously spans 0 down to -3.5 as you descend;
  // a topY sitting anywhere inside that range means the player's feet
  // pass THROUGH it mid-descent (not just at the entrance), and right at
  // that crossing instant `wall.topY<=feetY` flips the wall inactive for
  // that frame, letting a diagonal drift slip past sideways in the gap —
  // confirmed via a manual frame-by-frame trace after the first version of
  // this fix (topY=0) still let a sideways drift break through partway
  // down. topY=1.5 is never in the stair's traversed range, so there's no
  // crossing point to expose.
  walls.push(
    { minX: SUBWAY_STAIR.minX - 0.4, maxX: SUBWAY_STAIR.minX, minZ: SUBWAY_LANDING.minZ, maxZ: SUBWAY_STAIR.maxZ, topY: 1.5, bottomY: SUBWAY_LANDING.y - 1 },
    { minX: SUBWAY_STAIR.maxX, maxX: SUBWAY_STAIR.maxX + 0.4, minZ: SUBWAY_LANDING.minZ, maxZ: SUBWAY_STAIR.maxZ, topY: 1.5, bottomY: SUBWAY_LANDING.y - 1 },
  );

  // T1 3b/3c: the pit's SOUTH rim (z=-18, the landing's deep dead end) had
  // NO wall at all — only the two side walls above ever existed, covering
  // the x-edges, not this z-edge. A player walking north from z<-18
  // (reproduced from z=-20, x=-5.5) never touches a collider and just
  // walks straight off visually-solid street into the 3.5m-deep landing
  // once their XZ crosses into SUBWAY_LANDING's footprint — `groundHeightAt`
  // picks the landing's floor region there (priority 1, beats STREET_FLOOR's
  // priority 0) regardless of how far below the player currently stands,
  // and the OLD `newY <= ground` rule only grounds once the player has
  // already fallen down to meet it. Same topY/bottomY pattern as the side
  // walls above (well above street level; bottomY matches their own -1m
  // undercut) so this can't be skipped mid-fall the same way a tighter
  // bound could. The NORTH edge (z=-6, the stair's own street-level mouth)
  // is the intended entrance and stays open — no wall added there.
  walls.push({
    minX: SUBWAY_LANDING.minX,
    maxX: SUBWAY_LANDING.maxX,
    minZ: SUBWAY_LANDING.minZ - 0.4,
    maxZ: SUBWAY_LANDING.minZ,
    topY: 1.5,
    bottomY: SUBWAY_LANDING.y - 1,
  });

  // Tunnel side walls — span the RAMP's z-range too (TUNNEL_RAMP.minZ, not
  // just TUNNEL_FLOOR.minZ), not only the flat corridor beyond it. Without
  // this, sideways drift while walking DOWN the ramp itself (easy on a
  // touch stick, and the ramp's own footprint is wider than the walled
  // alley approach north of it) carried the player's XZ outside every
  // floor region while already below street level — STREET_FLOOR's own
  // height (0) is correctly excluded once nearY drops more than
  // STEP_TOLERANCE below it, so nothing else picks up the descent, and the
  // player free-fell until the -20 safety net silently teleported them
  // back to spawn. Confirmed by a 3-lens review. topY is 1.5, NOT the
  // tunnel's own low ceiling (tf.y+TUNNEL_HEIGHT = -0.4) — a first attempt
  // used that value (matching the flat corridor's own side walls) and
  // still let drift break through, because -0.4 sits INSIDE the ramp's own
  // 0-to--3 traversed range: the player's feet cross exactly through -0.4
  // as an ordinary part of walking down the slope (not a fast fall from
  // height, the OTHER failure mode the tall BACK wall below guards
  // against), and at that crossing instant the wall goes inactive for a
  // frame — confirmed by a manual frame trace showing the exact stall-
  // then-jump signature at y=-0.4. 1.5 is comfortably above the ramp's own
  // max height (0), so there's no point along the descent where it dips
  // inactive.
  const tf = TUNNEL_FLOOR;
  walls.push(
    { minX: tf.minX - 0.4, maxX: tf.minX, minZ: TUNNEL_RAMP.minZ, maxZ: tf.maxZ, topY: 1.5, bottomY: tf.y },
    { minX: tf.maxX, maxX: tf.maxX + 0.4, minZ: TUNNEL_RAMP.minZ, maxZ: tf.maxZ, topY: 1.5, bottomY: tf.y },
    // The back wall's topY reaches all the way up past the rooftop (16m),
    // not just the tunnel's own low ceiling — it sits directly under the
    // rooftop's own north edge, and a fall from up there covers this
    // wall's normal (tunnel-height-only) vertical span in a single physics
    // tick at speed, skipping the horizontal block entirely before ever
    // reaching a Y where it would have engaged (confirmed via a manual
    // frame trace: holding a straight line off the roof landed the player
    // at z=43, past the intended z=40 dead end). The side walls keep their
    // normal tunnel-height span — nothing overhead can fall through THEIR
    // XZ footprint from a comparable height, since the rooftop's own
    // footprint doesn't extend past x17 or this tunnel's x14-20.
    { minX: tf.minX, maxX: tf.maxX, minZ: tf.maxZ, maxZ: tf.maxZ + 0.4, topY: 40, bottomY: tf.y },
  );

  walls.push(...FIRE_ESCAPE_RAIL_SEGMENTS, FIRE_ESCAPE_LANDING_RAIL);

  // B-1 rooftop network — see that section's own comments above.
  walls.push(...ROOFTOP_D_RAIL_SEGMENTS, ROOFTOP_D_LANDING_RAIL, ROOFTOP_VANTAGE_TOWER_COLLIDER);

  return walls;
}

/**
 * S05's play-area boundary (PLAN-CITY §5: "boundaries"). Before this, the
 * only thing stopping a player from walking to the edge of `STREET_FLOOR`
 * (±40) was the floor itself running out — nothing marks the edge, so the
 * player falls into open space and is silently recovered by the
 * `position.y < -20` safety net in controller.ts, a "why did I just teleport
 * back to spawn" moment with no readable cause. A real wall, well inside
 * that ±40 edge (generous ~12m of open street beyond every existing
 * building/prop — Karan's own G1 words named exploring the map as his fun
 * driver, so the buffer is deliberately roomy, not a tight leash), replaces
 * the silent fall with a legible stop.
 *
 * `BOUNDARY` is centered on the block, comfortably outside `NavGrid`'s own
 * BOUNDS (x −30..30, z −24..32) and every `ai/habitats.ts` zone — no ground
 * enemy can ever reach it, so this can't interact with pathing/spawning.
 *
 * The north wall is the one side that ISN'T a plain unbroken span: it
 * leaves a gap over the utility tunnel's own x14-20 footprint (with a 0.4m
 * margin each side), because the tunnel already has its own reviewed,
 * load-bearing dead-end wall a little further out (layout.ts's own
 * `allWalls()`, `topY: 40` on the tunnel's back wall) — a second wall
 * crossing the corridor a couple meters short of that real dead end would
 * just be a confusing double stop in the one place the world already reads
 * as "this passage ends here." Same doorway-as-omitted-segment idiom
 * `buildingWalls()` already uses for BODEGA_DOOR.
 */
const BOUNDARY = { minX: -38, maxX: 38, minZ: -38, maxZ: 38 };
const BOUNDARY_HEIGHT = 2.4; // matches BODEGA_DOOR's height — a genuinely impassable register, well above VAULT_MAX_HEIGHT (1.1)
const BOUNDARY_THICKNESS = 0.4;
const BOUNDARY_NORTH_GAP_MIN = TUNNEL_RAMP.minX - 0.4; // 13.6
const BOUNDARY_NORTH_GAP_MAX = TUNNEL_RAMP.maxX + 0.4; // 20.4

export function boundaryWalls(): BoxCollider[] {
  const b = BOUNDARY;
  const t = BOUNDARY_THICKNESS;
  return [
    { minX: b.minX - t, maxX: b.minX, minZ: b.minZ, maxZ: b.maxZ, topY: BOUNDARY_HEIGHT, bottomY: 0 }, // west
    { minX: b.maxX, maxX: b.maxX + t, minZ: b.minZ, maxZ: b.maxZ, topY: BOUNDARY_HEIGHT, bottomY: 0 }, // east
    { minX: b.minX, maxX: b.maxX, minZ: b.minZ - t, maxZ: b.minZ, topY: BOUNDARY_HEIGHT, bottomY: 0 }, // south
    { minX: b.minX, maxX: BOUNDARY_NORTH_GAP_MIN, minZ: b.maxZ, maxZ: b.maxZ + t, topY: BOUNDARY_HEIGHT, bottomY: 0 }, // north, west of the tunnel gap
    { minX: BOUNDARY_NORTH_GAP_MAX, maxX: b.maxX, minZ: b.maxZ, maxZ: b.maxZ + t, topY: BOUNDARY_HEIGHT, bottomY: 0 }, // north, east of the tunnel gap
  ];
}

export const MARKERS: MarkerSpec[] = [
  { id: 'spawn', x: SPAWN_POSITION.x, y: 0, z: SPAWN_POSITION.z },
  { id: 'bodega_door', x: BODEGA_DOOR.x, y: 0, z: BODEGA_DOOR.z },
  { id: 'rooftop', x: 11.5, y: ROOFTOP.y, z: 17 },
  { id: 'park_pocket', x: -17, y: 0, z: 17 },
  { id: 'subway_landing', x: -5.5, y: SUBWAY_LANDING.y, z: -16 },
  { id: 'utility_tunnel', x: 17, y: TUNNEL_FLOOR.y, z: 34 },
  { id: 'construction_pocket', x: 0, y: 0, z: -32 },
  // B-1 rooftop network — 'rooftop_d' deliberately lands clear of ROOFTOP_VANTAGE_TOWER's own footprint (x[15.8,18.2] z[-25.2,-22.8]) so a dev-teleport there tests the plain deck, not the tower.
  { id: 'rooftop_b', x: 21, y: ROOFTOP_B.y, z: 17 },
  { id: 'rooftop_d', x: 20, y: ROOFTOP_D.y, z: -17 },
  { id: 'rooftop_vantage', x: ROOFTOP_VANTAGE_MARKER.x, y: ROOFTOP_VANTAGE_MARKER.y, z: ROOFTOP_VANTAGE_MARKER.z },
];

// --- S10: construction pocket (PLAN-CITY §5's "repeatable Gridlock lair in
// the construction pocket") ---
// C02 board 6 (the habitat map) names "the Gridlock lair contained in the
// construction pocket" as its own zone — never built as real geometry
// through S01-S09 (S06's GRIDLOCK_ARENA is a purely LOGICAL trigger,
// world/arena.ts's own header comment explicitly deferring "a genuine
// physical gate" to "a future Wave C session building the real lair," which
// this is). Centered on GRIDLOCK_ARENA's own (0,-32) rather than introducing
// a second coordinate for the same place — the arena and the pocket that
// visually contains it are the same spot by construction, not by
// coincidence. Sized to comfortably contain the arena's r5.5 circle plus
// walking room, inside the open street strip S06's own arena comment
// already surveyed clear (z −38..−26, the full width between the boundary
// wall and building_c/d's south edge) — re-confirmed here against every
// other zone: nearest habitat (`street_curbs`, ai/habitats.ts) ends at
// z=−22, nearest building wall at z=−26, boundary wall at z=−38.4.
export const CONSTRUCTION_POCKET = { minX: -13, maxX: 13, minZ: -37.5, maxZ: -26.5 };

/**
 * `blk_scaffold` perimeter dressing (S10) — placed around the pocket's own
 * perimeter (CONSTRUCTION_POCKET above), rotated to face inward. Originally
 * shipped visual-only (S10's own call, weighing a hard physical gate here
 * against "every new physical collider in this block has cost a review
 * round in the past") — T3-3 (PLAN-CITY2 §2, §0.3) explicitly revokes that
 * exemption ("it's just decoration" no longer excuses a walk-through prop
 * at street level), so each of the 6 instances below now has a matching
 * collider too — see `SCAFFOLD_COLLIDERS` further down this file.
 */
export interface ScaffoldSpec {
  x: number;
  z: number;
  rotationY: number;
}
export const CONSTRUCTION_SCAFFOLDS: ScaffoldSpec[] = [
  { x: -11, z: -32, rotationY: Math.PI / 2 }, // west edge, facing east into the pocket
  { x: 11, z: -32, rotationY: -Math.PI / 2 }, // east edge, facing west
  { x: -9, z: -36.5, rotationY: 0 }, // south edge (near the boundary), facing north
  { x: 9, z: -36.5, rotationY: 0 },
  { x: -9, z: -27.5, rotationY: Math.PI }, // north edge (near the buildings), facing south
  { x: 9, z: -27.5, rotationY: Math.PI },
];

/**
 * T3-3 companion to `CONSTRUCTION_SCAFFOLDS` above — each box measured LIVE
 * against the real `blk_scaffold.glb` instance at its actual placed/rotated
 * position (a world-space bounding-box probe run in the browser), not
 * guessed. The fight's own approach lane is untouched: every box sits
 * outside the center strip (x∈[-7.75,7.75]) at every Z, so a player can
 * still walk straight up the middle from either the north or south side —
 * only each individual scaffold clump itself stops being walk-through.
 * Hand-authored literals like every other collider in this file (BUILDINGS/
 * PARK_PROPS/SUBWAY_GATE) — if `CONSTRUCTION_SCAFFOLDS` above ever moves,
 * re-measure and update these to match.
 */
export const SCAFFOLD_COLLIDERS: BoxCollider[] = [
  { minX: -12.5, maxX: -8.4, minZ: -33.25, maxZ: -30.75, bottomY: 0, topY: 2.53 }, // west
  { minX: 8.4, maxX: 12.5, minZ: -33.25, maxZ: -30.75, bottomY: 0, topY: 2.53 }, // east
  { minX: -10.25, maxX: -7.75, minZ: -38, maxZ: -33.9, bottomY: 0, topY: 2.53 }, // south-west
  { minX: 7.75, maxX: 10.25, minZ: -38, maxZ: -33.9, bottomY: 0, topY: 2.53 }, // south-east
  { minX: -10.25, maxX: -7.75, minZ: -30.1, maxZ: -26, bottomY: 0, topY: 2.53 }, // north-west
  { minX: 7.75, maxX: 10.25, minZ: -30.1, maxZ: -26, bottomY: 0, topY: 2.53 }, // north-east
];

/**
 * T3-3 (PLAN-CITY2 §2, §0.3): C04 street-dressing props (block.ts's own
 * `attachStreetProps()`) shipped with ZERO collision — "pure street
 * dressing" was an explicit, deliberate S10-era call (its own header
 * comment: "PLAN-CITY's own gray-box law never called for placeholder
 * lamps/signals/trash cans"), now explicitly revoked by §0.3 — Karan walked
 * straight through the parked taxi and called it out by name. Every box
 * below was measured LIVE against each prop's real, currently-loaded GLB
 * instance at its EXACT placed position/rotation in `attachStreetProps()`
 * (a world-space bounding-box probe run in the browser), not guessed —
 * same discipline as `SCAFFOLD_COLLIDERS` above. Hand-authored literals, not
 * a live rotation-aware formula — if a future session moves any prop's
 * `pos`/`rotationY` in block.ts, re-measure and update the matching box.
 *
 * T3-3 review finding (LOW, don't-break-list lens, CONFIRMED but NOT live
 * today): these boxes were cross-checked against the dive_pigeon habitat
 * home point that actually landed inside one (fixed — see the food-cart
 * entry's own comment below) but were NOT systematically cross-checked
 * against every `ai/habitats.ts` rectangle the way that file's own header
 * comment requires of ITS side of this relationship ("hand-checked against
 * BUILDINGS/ALLEY/ROOFTOP... to stay clear of solid geometry"). With
 * TODAY's fixed seed (0x8e17) every other habitat's own rolled home point
 * happens to clear these colliders by real margin (confirmed live) — but
 * nothing enforces this going forward. A future session changing any
 * habitat's area bounds, `SPAWN_JITTER`, the RNG seed, or `maxPopulation`
 * should re-verify every habitat's possible home points against this file's
 * own colliders, the same direction `ai/habitats.ts`'s own gotcha comment
 * already asks of ITS future changes against `layout.ts`.
 */
export const STREET_PROP_COLLIDERS: BoxCollider[] = [
  // prp_lamp.glb x2 — block.ts attachStreetProps pos [-6.8,0,-30] / [6.8,0,30]
  { minX: -7.43, maxX: -6.17, minZ: -30.3, maxZ: -29.7, bottomY: 0, topY: 3.62 },
  { minX: 6.17, maxX: 7.43, minZ: 29.7, maxZ: 30.3, bottomY: 0, topY: 3.62 },
  // prp_signal.glb — pos [5,0,5]
  { minX: 4.1, maxX: 5.9, minZ: 4.66, maxZ: 5.17, bottomY: 0, topY: 2.69 },
  // prp_trash_can.glb x2 — pos [9,0,6] / [-15,0,10]
  { minX: 8.49, maxX: 9.51, minZ: 5.5, maxZ: 6.5, bottomY: 0, topY: 1.31 },
  { minX: -15.51, maxX: -14.49, minZ: 9.5, maxZ: 10.5, bottomY: 0, topY: 1.31 },
  // prp_taxi.glb — Karan's own reported "walk-through car" — pos [6.8,0,-20], rotationY: PI/2
  { minX: 5.95, maxX: 7.65, minZ: -21.82, maxZ: -18.19, bottomY: 0, topY: 1.67 },
  // prp_food_cart.glb — pos [-15,0,14] (T3-3 review fix: shifted south 4m
  // from its original [−15,0,18] — see block.ts's attachStreetProps comment
  // for why) — same measured footprint incl. its umbrella/canopy top,
  // translated by the identical (0,0,−4) delta as the visual placement.
  { minX: -16.28, maxX: -13.72, minZ: 12.72, maxZ: 15.28, bottomY: 0, topY: 3.12 },
];

// --- S10: hidden stash (utility tunnel dead end) ---
// C02 board 1 names "hidden stash" as its own zone, tucked past the tunnel's
// main corridor. Placed at the tunnel's own dead end (TUNNEL_FLOOR's
// maxZ=40, per layout.ts's existing const above) rather than along its main
// walked path, so finding it rewards actually exploring the corridor's full
// length — matching Karan's own G1 words naming exploration as his fun
// driver. `z: 38.5` keeps a 1.5m margin off the real dead-end back wall
// (allWalls()'s own tf.maxZ+0.4 collider) so the interact radius below never
// needs the player to stand inside geometry.
// Wave P-4 (PLAN-CITY3 §3): x shifted 17 -> 15.5 (west, still inside
// TUNNEL_FLOOR's x[14,20]) so Wave K's future tunnel-breach entrance
// interactable (the corridor's far end, near this same dead end) has real
// separation from this one's own 1.8-radius prompt circle instead of
// landing on top of it.
export const HIDDEN_STASH_MARKER = { x: 15.5, z: 38.5 };

/**
 * B-2 "Enterable interiors" (PLAN-CITY2 §3) — a grate/hatch down to the
 * basement vestibule (world/interiorBasement.ts), on the tunnel's own
 * corridor at z=33: past TUNNEL_RAMP's own z-range (26-29, so it's already
 * on the flat floor, not the slope) and 5.5m short of HIDDEN_STASH_MARKER's
 * z=38.5 (real separation between the two tunnel interactables — no
 * trigger-radius overlap at either's own ~1.4-2.2 radius). x=17 is
 * TUNNEL_FLOOR's own center ((14+20)/2) — Wave P-4 shifted
 * HIDDEN_STASH_MARKER's own x to 15.5 (clearing room for a future Wave K
 * breach entrance near the stash), so the two no longer share an x; the
 * real separation guarantee was always the 5.5m z-gap above, not the x
 * match, and that gap is untouched. Interact-button only (world/interact.ts's `enter_basement` action) —
 * see interiorBasement.ts's own header comment for why a walk-over
 * auto-trigger would be the wrong UX here (this sits ON the main corridor to
 * the stash, unlike a storefront door).
 */
export const BASEMENT_HATCH = { x: 17, z: 33 };

// --- S10: locked Corona exit sign (street level, distinct from S05's own
// subway-gate seam) ---
// C02 board 1 lists "subway entrance"/"both tunnel entries" and "locked
// Corona exit" as FOUR separate named zones, not one repeated idea — S05's
// existing `SUBWAY_GATE` seam (world/interact.ts's `subway_seam`) already
// covers the underground route; this is the street-level one, gating the
// east street arm where it would otherwise run off the edge of the built
// block. Placed on the sidewalk margin, well inside `boundaryWalls()`'s own
// east wall (x=38+0.4) with real approach room, and clear of every prop in
// `attachStreetProps()` (block.ts) and every habitat in ai/habitats.ts.
export const CORONA_EXIT_GATE: PropSpec = {
  id: 'corona_exit_gate',
  x: 34,
  z: 0,
  width: 0.3,
  depth: STREET_HALF_WIDTH * 2,
  height: 1.6,
  color: 0xd9a441,
};

/**
 * S10 review finding (HIGH, independently confirmed by two review lenses):
 * a collider sized to CORONA_EXIT_GATE's own visual footprint (the 8m paved
 * lane, `STREET_HALF_WIDTH*2`) only blocks the exact street centerline — the
 * open sidewalk/pavement north and south of it, all the way out to the real
 * play boundary, has no collider at all, so "locked" was trivially walked
 * around at any z outside [-4,4] (confirmed live: a lateral detour reaches
 * x≈37.7 with zero collision response). A "locked exit" that only gates the
 * paved lane isn't actually locked. Fixed with a SEPARATE, much wider
 * collider at the same X — z∈[-38,38], matching `boundaryWalls()`'s own
 * BOUNDARY z-extent exactly (that function's own `b.minZ/b.maxZ`, not
 * re-derived independently, so the two can't silently drift apart) — while
 * the VISUAL gate/sign above stay at their original street-scale size. An
 * invisible wall extension beyond the themed prop is a standard, accepted
 * pattern for this exact "the road continues but this session doesn't build
 * it" seam (block.ts never renders anything for this extended span, so nothing
 * looks wrong — the player simply can't walk past the sign in either
 * direction along this X, the same experience the visual gate alone was
 * supposed to create).
 */
export const CORONA_EXIT_BARRIER: BoxCollider = {
  minX: CORONA_EXIT_GATE.x - CORONA_EXIT_GATE.width / 2,
  maxX: CORONA_EXIT_GATE.x + CORONA_EXIT_GATE.width / 2,
  minZ: -38,
  maxZ: 38,
  topY: CORONA_EXIT_GATE.height,
  bottomY: 0,
};

// --- B-3 (PLAN-CITY2 §3, Astoria detail pass, C06) ---
// Every new FloorRegion/BoxCollider below is wired in via block.ts's
// `BlockPhysics` constructor array literals (`[...FLOORS, ...]` /
// `[...allWalls(), ...]`), the same pattern B-2 used for the laundromat/
// basement — deliberately NOT appended into this file's own `FLOORS`
// array/`allWalls()` function/`MARKERS` array, since B-1 (rooftop network,
// concurrent session) is actively extending those same three exports for
// its own new roof regions/rail/landmark marker. Every new const below is
// its own standalone export, net-new names, zero edits to any existing
// line above this point.
//
// The elevated line — dimension table per city/briefs/C06-astoria-nostalgia-
// set.md §B: pier spacing = deck tile length = 8.0m, deck 4.0m wide,
// pier girder top 7.0m, station platform 7.4m. Runs along the east-west
// street (world Z=0) so it crosses the main intersection, matching how the
// real N/W line runs elevated over a street rather than floating apart from
// it. Piers start at x=-16, NOT the next 8m-spaced position west (x=-24):
// a pier there would drop its two COL_box_column colliders (z≈±1.45,
// measured live off the delivered GLB below) squarely inside
// ai/habitats.ts's `elite_patrol` zone (x-26..-18, z-6..6) — the Rat King
// roamer's own patrol area — the same class of habitat/collider conflict
// T3-3 already found and fixed once for the food cart (STATUS-CITY.md's T3
// log: "cross-check any new collider against every ai/habitats.ts rectangle,
// not just the ones a task names").
export const EL_LINE_PIER_XS = [-16, -8, 0, 8, 16, 24, 32];
export const EL_LINE_RUN_MIN_X = -16;
export const EL_LINE_RUN_MAX_X = 32; // 6 deck tiles, contiguous, cover this whole span
export const EL_PIER_HEIGHT = 7.0; // girder top — matches the brief's dimension table and the delivered blk_el_pier.glb's own bbox (size.y=7.0) exactly

/**
 * Measured live off `blk_el_pier.glb` (dump-col-nodes probe against the
 * delivered GLB's own node translations/scales — collision Empty scale =
 * half-extent, per ASSET-CONTRACT §5): two `COL_box_column` nodes at local
 * (∓1.45, 3.5, 0), half-extents (0.175, 3.5, 0.225). Piers are placed with
 * rotationY=π/2 (block.ts) so the columns straddle the street's Z-width
 * instead of running along it — under that rotation local (x,z) maps to
 * world (z,-x), so local X (the 1.45 span) becomes a world-Z offset and
 * local Z (the 0.225 half-extent) becomes the world-X half-width.
 */
const EL_PIER_COLUMN_HALF_WIDTH = 0.225; // world-X half-extent post-rotation (was the column's local-Z half-extent)
const EL_PIER_COLUMN_Z_CENTER = 1.45; // world-Z offset post-rotation (was the column's local-X position)
const EL_PIER_COLUMN_HALF_DEPTH = 0.175; // world-Z half-extent post-rotation (was the column's local-X half-extent)
export const EL_PIER_COLLIDERS: BoxCollider[] = EL_LINE_PIER_XS.flatMap((x) => [
  {
    minX: x - EL_PIER_COLUMN_HALF_WIDTH,
    maxX: x + EL_PIER_COLUMN_HALF_WIDTH,
    minZ: EL_PIER_COLUMN_Z_CENTER - EL_PIER_COLUMN_HALF_DEPTH,
    maxZ: EL_PIER_COLUMN_Z_CENTER + EL_PIER_COLUMN_HALF_DEPTH,
    bottomY: 0,
    topY: EL_PIER_HEIGHT,
  },
  {
    minX: x - EL_PIER_COLUMN_HALF_WIDTH,
    maxX: x + EL_PIER_COLUMN_HALF_WIDTH,
    minZ: -EL_PIER_COLUMN_Z_CENTER - EL_PIER_COLUMN_HALF_DEPTH,
    maxZ: -EL_PIER_COLUMN_Z_CENTER + EL_PIER_COLUMN_HALF_DEPTH,
    bottomY: 0,
    topY: EL_PIER_HEIGHT,
  },
]);

/**
 * `blk_el_deck.glb`'s manifest `placementY` (7.4) is, per this file's
 * established convention (blk_subway_entrance/blk_tunnel_kit — block.ts's
 * attachSubwayEntrance/attachTunnelKit set `position.y` directly to the
 * delivered placementY), where `position.y` gets set — NOT where the
 * walkable surface itself ends up. Measured live off the GLB: `COL_box_
 * decktop` sits at local Y=0.42 (a thin track-bed slab above the deck's own
 * min-Y=0 underside, with railposts/toprail continuing up from there to the
 * model's overall 1.22 top).
 *
 * REVIEW FIX (HIGH, correctness lens — the first version of this comment
 * was wrong): setting `position.y` directly to the brief's own placementY
 * (7.4) floated the deck's underside 0.4m above `blk_el_pier`'s own real
 * girder top (measured live: the pier's `girder` mesh tops out at exactly
 * 7.0, matching the dimension table's "Pier height (girder top) 7.0m" row)
 * — a visible gap between the deck and the piers meant to support it. The
 * `COL_box_decktop` Empty's own translation.y (0.42) is also NOT the real
 * walking surface: it's the CENTER of a thin collision-proxy box
 * (half-extent 0.02), not the visible mesh's own top. A live re-measure of
 * the actual `walk_deck` MESH (not the proxy) puts its real top at local Y
 * 0.400 exactly. Correct derivation: rest the deck's underside ON the
 * pier's girder top (`position.y = EL_PIER_HEIGHT`), giving a walkable
 * surface at `EL_PIER_HEIGHT + 0.40 = 7.4` — matching the girder, the
 * brief's dimension table, AND NOTES.md's own precise claim for the station
 * stair ("reaches its exact 7.4m platform surface") all at once. The
 * harness's own deck-height check was circular (compared this constant to
 * itself) until this same review fix added a real mesh-bbox probe.
 */
export const EL_DECK_PLACEMENT_Y = EL_PIER_HEIGHT;
export const EL_DECK_WALK_Y = EL_DECK_PLACEMENT_Y + 0.4;
export const EL_DECK_HALF_WIDTH = 2; // 4.0m wide, matches the dimension table and the GLB's own local Z half-extent

/**
 * REVIEW FLAG (MEDIUM, don't-break-list lens — not fixed here, flagged for
 * awareness): this is the first FloorRegion ever placed directly over the
 * main street intersection at street-adjacent height. `controller.ts`'s
 * `teleport()` (used by the dev-tap tool AND main.ts's suspended-run resume
 * path) queries `groundHeightAt` with a deliberately generous tolerance
 * ("land on whatever's below, any level") — since `STREET_FLOOR` always
 * qualifies as a candidate everywhere (by design, collision.ts's own
 * comment), and this region's higher default priority wins ties, a
 * teleport/resume landing anywhere in this deck's footprint (x -16..32,
 * z -2..2 — which includes the spawn-adjacent main intersection) now
 * resolves onto the DECK (y≈7.4) instead of the street (y=0). Confirmed
 * real for a harness check that explicitly teleports into this band
 * (fixed at that call site, harness/run.mjs); NOT confirmed to affect any
 * currently-shipped gameplay path today, but a save made while standing in
 * this XZ band would resume on the deck per the same mechanism. Normal
 * WALKING is unaffected (`STEP_TOLERANCE`=0.2 never picks up a 7.4m region
 * from street level). Not fixed at the source here because the real fix
 * needs either a change to teleport()'s own shared tolerance semantics
 * (used by many unrelated callers) or a change to the suspended-run resume
 * call site in main.ts (owned by a concurrent B-2 session as of this
 * writing) — narrower than this pass's own scope. Worth a future session's
 * attention if it's ever confirmed to bite a real save.
 */
export const EL_DECK_FLOOR: FloorRegion = {
  kind: 'flat',
  minX: EL_LINE_RUN_MIN_X,
  maxX: EL_LINE_RUN_MAX_X,
  minZ: -EL_DECK_HALF_WIDTH,
  maxZ: EL_DECK_HALF_WIDTH,
  y: EL_DECK_WALK_Y,
};

/**
 * Station stair — connects the sidewalk (z=11, street level) to the deck's
 * own south edge (z=2, EL_DECK_WALK_Y) in exactly a 9m run, matching the
 * brief's "footprint ≤3m wide × 9m run" dimension. `blk_el_station_stair`'s
 * own delivered `COL_box_stair_ramp` is a genuinely TILTED box (Blender X
 * rotation -39.43°, per report.json/NOTES.md) — this engine's FloorRegion
 * system only models axis-aligned ramps (ASSET-CONTRACT §5's own
 * "ramp-approximated" allowance, the same technique FIRE_ESCAPE_RAMP/
 * TUNNEL_RAMP/SUBWAY_STAIR already use), so the tilted collider isn't
 * consumed directly — its own numbers cross-check cleanly against the
 * brief's stated footprint (slope length 2×5.826=11.65m; at 39.43°,
 * horizontal run = 11.65×cos(39.43°) ≈ 9.0m and rise = 11.65×sin(39.43°) ≈
 * 7.4m — both match), confirming a `{minZ:2, maxZ:11, y0:EL_DECK_WALK_Y,
 * y1:0}` axis-aligned ramp is a faithful approximation, not a guess.
 * x-span (26.5–29.5) sits between piers at x=24 and x=32, clear of every
 * building/habitat (checked against BUILDINGS/ai/habitats.ts by hand).
 */
export const EL_STATION_STAIR: FloorRegion = { kind: 'ramp', minX: 26.5, maxX: 29.5, minZ: 2, maxZ: 11, y0: EL_DECK_WALK_Y, y1: 0, axis: 'z' };

/**
 * CP1 stair-blocker fix (Wave E, PLAN-CITY3 §6/STATUS-CITY.md's diagnosed
 * "stair blocker"). The two side walls here used to be monolithic
 * ground-to-sky boxes (bottomY=-1, topY=EL_DECK_WALK_Y+1) — a direct copy of
 * SUBWAY_STAIR's own side-wall template a few hundred lines up. That
 * template is correct for SUBWAY_STAIR because it descends BELOW street
 * level into a pit: nothing walkable ever exists beside a below-grade
 * descent at street height, so sealing top-to-bottom costs nothing. This
 * stair is the opposite shape — it climbs UP and OVER real, walkable
 * sidewalk — so a monolithic box (bottomY=-1 is NEVER vertically clear of a
 * standing player, since `resolveHorizontal`'s skip test needs
 * `bottomY >= headY`) permanently fenced the sidewalk along the stair's
 * whole 9m run, even for a pedestrian who was never on the stair at all.
 * Probe-verified: a westbound pedestrian at street level got stopped dead
 * at x=25.900 (26.5-RAIL_THICKNESS+PLAYER_RADIUS) for the wall's entire
 * z[2,11] span, regardless of z.
 *
 * Fixed the same way T1 fixed the identical bug class on
 * FIRE_ESCAPE_RAIL_SEGMENTS/ROOFTOP_D_RAIL_SEGMENTS above: stepped segments
 * whose bottomY/topY only cover the height the stair itself actually spans
 * across that segment (plus RAIL_BOTTOM_MARGIN/RAIL_TOP_MARGIN), floored at
 * STREET_INERT_BOTTOM_Y so segments near true street level go vertically
 * inert against a standing pedestrian — same idiom, same constants.
 *
 * Two differences from copying FIRE_ESCAPE_RAIL_SEGMENTS's loop verbatim:
 * (1) EL_STATION_STAIR climbs as Z DECREASES (y0=EL_DECK_WALK_Y at minZ=2,
 * y1=0 at maxZ=11) — the opposite direction of FIRE_ESCAPE_RAMP/
 * ROOFTOP_D_RAMP (both climb as Z increases). Their shared formula
 * (bottomY from h(zStart), topY from h(zEnd)) silently assumes an
 * ascending ramp; copied blind onto a descending one it inverts (bottomY
 * above topY on some segments). Using min/max of the segment's own two
 * endpoint heights is the direction-agnostic generalization — on an
 * ascending ramp h(zStart) is already the min, so this reduces to the
 * exact same two lines there; it's a strict superset of the existing
 * idiom, not a divergent one. (2) the stair's 9m run isn't a whole multiple
 * of RAIL_SEGMENT_LENGTH (2m) the way the 16m/12m ramps are — the last
 * segment is capped at the ramp's own maxZ instead of assuming an exact
 * fit, so this can't silently grow the footprint 1m past where the real
 * stair (and the old monolithic wall) ended.
 */
export const EL_STATION_SIDE_WALLS: BoxCollider[] = (() => {
  const r = EL_STATION_STAIR;
  const sides: Array<[number, number]> = [
    [26.5 - RAIL_THICKNESS, 26.5], // west side (was the monolithic 26.5-0.3..26.5 box)
    [29.5, 29.5 + RAIL_THICKNESS], // east side (was the monolithic 29.5..29.5+0.3 box)
  ];
  const segCount = Math.ceil((r.maxZ - r.minZ) / RAIL_SEGMENT_LENGTH);
  const walls: BoxCollider[] = [];
  for (const [minX, maxX] of sides) {
    for (let i = 0; i < segCount; i++) {
      const zStart = r.minZ + i * RAIL_SEGMENT_LENGTH;
      const zEnd = Math.min(zStart + RAIL_SEGMENT_LENGTH, r.maxZ);
      const h0 = rampHeightAt(r, zStart);
      const h1 = rampHeightAt(r, zEnd);
      walls.push({
        minX,
        maxX,
        minZ: zStart,
        maxZ: zEnd,
        bottomY: Math.max(Math.min(h0, h1) - RAIL_BOTTOM_MARGIN, STREET_INERT_BOTTOM_Y),
        topY: Math.max(h0, h1) + RAIL_TOP_MARGIN,
      });
    }
  }
  return walls;
})();

/**
 * Deck-edge rails: the delivered model has visible `toprail`/`railpost`
 * geometry (no separate COL node for it — only `COL_box_decktop`, the
 * walking surface, per the brief), so per §0.4 ("everything visible ...
 * has a collider or is removed") the visible railing needs matching
 * collision, same reasoning that put FIRE_ESCAPE_RAIL_SEGMENTS on the fire
 * escape ramp. Split into segments with a gap at the station stair's own
 * x-range (26.5–29.5) on the platform-facing (+Z) edge only, so the stair
 * can actually connect to the deck instead of running into an invisible
 * rail; the far (−Z) edge, which nothing connects to, stays one unbroken
 * span.
 */
const EL_DECK_RAIL_BOTTOM_Y = EL_DECK_WALK_Y - 0.5;
const EL_DECK_RAIL_TOP_Y = EL_DECK_WALK_Y + JUMP_APEX + 0.25; // same margin RAIL_TOP_MARGIN already establishes for the fire-escape rail
const EL_DECK_RAIL_THICKNESS = 0.3;
export const EL_DECK_RAILS: BoxCollider[] = [
  // −Z edge (no stair connects here) — one unbroken span across the whole run
  { minX: EL_LINE_RUN_MIN_X, maxX: EL_LINE_RUN_MAX_X, minZ: -EL_DECK_HALF_WIDTH - EL_DECK_RAIL_THICKNESS, maxZ: -EL_DECK_HALF_WIDTH, bottomY: EL_DECK_RAIL_BOTTOM_Y, topY: EL_DECK_RAIL_TOP_Y },
  // +Z edge — gapped at the station stair (26.5–29.5)
  { minX: EL_LINE_RUN_MIN_X, maxX: 26.5, minZ: EL_DECK_HALF_WIDTH, maxZ: EL_DECK_HALF_WIDTH + EL_DECK_RAIL_THICKNESS, bottomY: EL_DECK_RAIL_BOTTOM_Y, topY: EL_DECK_RAIL_TOP_Y },
  { minX: 29.5, maxX: EL_LINE_RUN_MAX_X, minZ: EL_DECK_HALF_WIDTH, maxZ: EL_DECK_HALF_WIDTH + EL_DECK_RAIL_THICKNESS, bottomY: EL_DECK_RAIL_BOTTOM_Y, topY: EL_DECK_RAIL_TOP_Y },
];

// ============================================================================
// Wave E (PLAN-CITY3 §6, "THE STATION"): C11 train/strip/bumper wiring +
// platform content. Data-only exports — the actual habitat/checkpoint/
// interactable ENTRIES live in ai/habitats.ts/world/checkpoints.ts/
// world/interact.ts (this wave's own shared-file territory per §13), and the
// GLB placement itself lives in block.ts's own new attachElTrainAndStation().
// ============================================================================

// --- E-2: the train (C11 WIRING NOTE) ---
// Center (-8, 7.52, 0), unrotated (local X = world X, so its own nose —
// COL_box_body's local +X face — lands exactly at world x=0). 16m car
// spans x[-16,0], flush with EL_LINE_RUN_MIN_X — the line's own dead west
// end, matching "the line ends at a parked train."
export const EL_TRAIN_POS = { x: -8, z: 0 };
export const EL_TRAIN_PLACEMENT_Y = 7.52;
const EL_TRAIN_NOSE_X = 0;

/**
 * The train's own delivered `COL_box_body` — independently re-measured off
 * the GLB (not trusted from report.json alone): center local (0,1.6,0),
 * half-extents (8,1.6,1.75), unrotated placement (local = world offset).
 * This is the PRIMARY seal per the WIRING NOTE — see
 * EL_TRAIN_NOSE_COLLIDER's own comment for why it alone already blocks
 * every reachable lateral approach along the train's whole 16m length.
 */
export const EL_TRAIN_COLLIDER: BoxCollider = {
  minX: EL_TRAIN_POS.x - 8,
  maxX: EL_TRAIN_POS.x + 8,
  minZ: EL_TRAIN_POS.z - 1.75,
  maxZ: EL_TRAIN_POS.z + 1.75,
  bottomY: EL_TRAIN_PLACEMENT_Y,
  topY: EL_TRAIN_PLACEMENT_Y + 3.2,
};

/**
 * MANDATORY second seal (C11 WIRING NOTE), discharging E-1's west end-cap
 * obligation. The train's own delivered `COL_box_body` (half-extents
 * 8/1.6/1.75, independently re-measured off the GLB — see this wave's own
 * report — center (0,1.6,0) relative to EL_TRAIN_POS) is the PRIMARY seal:
 * its z half-extent (1.75) expanded by PLAYER_RADIUS (0.3) reaches z=±2.05,
 * which already exceeds the deck's own rail-clamped max reachable
 * player-CENTER z (±1.7, since EL_DECK_RAILS' own outer edge sits at
 * z=±2.0 and blocks any center beyond z=±(2.0-PLAYER_RADIUS)=±1.7) — so the
 * train's body alone already closes every lateral approach along its whole
 * 16m length, from either direction. This collider is still built exactly
 * as the WIRING NOTE requires: an explicit, position-independent guarantee
 * at the nose line that doesn't rely on that more subtle rail-interaction
 * arithmetic staying true if PLAYER_RADIUS or the rail's own clamp ever
 * changes later. Full deck width (z[-2,2], wider than the train's own
 * z[-1.75,1.75]) at the nose line, same rail-height convention as
 * EL_DECK_RAILS (defined above in this same file).
 */
export const EL_TRAIN_NOSE_COLLIDER: BoxCollider = {
  minX: EL_TRAIN_NOSE_X,
  maxX: EL_TRAIN_NOSE_X + EL_DECK_RAIL_THICKNESS,
  minZ: -EL_DECK_HALF_WIDTH,
  maxZ: EL_DECK_HALF_WIDTH,
  bottomY: EL_DECK_RAIL_BOTTOM_Y,
  topY: EL_DECK_RAIL_TOP_Y,
};

// --- E-2: track-tie/rail overlay strip — 6 instances per the WIRING NOTE,
// including under the train (pure visual dressing, zero collision by
// design per NOTES.md — the 0.12m rail heads stay visual-only). Placement Y
// is EL_DECK_WALK_Y (7.40), NOT the train's 7.52 — raycastGroundDownAt must
// keep reading 7.40 off the real deck surface, never off a rail head, so
// these instances are never pushed into block.ts's groundMeshes.
export const EL_TRACK_STRIP_XS = [-12, -4, 4, 12, 20, 28];
export const EL_TRACK_STRIP_PLACEMENT_Y = EL_DECK_WALK_Y;

// --- E-1: east end-cap (the west end is discharged by EL_TRAIN_NOSE_COLLIDER
// above — E-2's own train/nose must exist before this is "both ends done").
// Bumper placed unrotated so its hazard stripes/red lamp (local -X) face
// WEST, back face (local +X, world x = EL_BUMPER_POS.x+0.6) landing at
// 31.8 — clear of the station stair's own x<=29.5 landing.
export const EL_BUMPER_POS = { x: 31.2, z: 0 };
export const EL_BUMPER_PLACEMENT_Y = EL_DECK_WALK_Y;

/**
 * The bumper's own delivered `COL_box_bumper` — independently re-measured
 * off the GLB: center local (0,0.65,0), half-extents (0.6,0.65,1),
 * unrotated placement. Matches the full visible extent exactly (no
 * vault-height trap — rise from the deck's own walk surface is 1.30m,
 * clearing VAULT_MAX_HEIGHT's 1.1m, per the WIRING NOTE).
 */
export const EL_BUMPER_COLLIDER: BoxCollider = {
  minX: EL_BUMPER_POS.x - 0.6,
  maxX: EL_BUMPER_POS.x + 0.6,
  minZ: EL_BUMPER_POS.z - 1,
  maxZ: EL_BUMPER_POS.z + 1,
  bottomY: EL_BUMPER_PLACEMENT_Y,
  topY: EL_BUMPER_PLACEMENT_Y + 1.3,
};

/**
 * E-1's own end-cap rail, distinct from the bumper GLB's own
 * `COL_box_bumper` (which only spans the center 2m — z[-1,1] — of the
 * deck's 4m width). The bumper alone leaves a real, walkable ~1m gap on
 * EACH side (z[1,2] and z[-2,-1], with the rail's own clamp only pulling a
 * player-center in to ±1.7) where a player could walk straight past it and
 * off the true east edge (x=32) without ever touching the bumper. This
 * full-width collider — derived from EL_DECK_RAILS' own height/thickness
 * constants exactly as E-1's spec requires ("derived from EL_DECK_RAILS'
 * own constants") — closes that, the same way FIRE_ESCAPE_LANDING_RAIL/
 * ROOFTOP_D_LANDING_RAIL close an analogous "the rail delivers you right up
 * to an unguarded edge" gap elsewhere in this file. Placed right at the
 * deck's own true end (EL_LINE_RUN_MAX_X=32), leaving the bumper's own
 * visual dressing 0.2m short of it (back face 31.8) — matches the WIRING
 * NOTE's own number exactly.
 */
export const EL_DECK_EAST_END_CAP: BoxCollider = {
  minX: EL_LINE_RUN_MAX_X,
  maxX: EL_LINE_RUN_MAX_X + EL_DECK_RAIL_THICKNESS,
  minZ: -EL_DECK_HALF_WIDTH,
  maxZ: EL_DECK_HALF_WIDTH,
  bottomY: EL_DECK_RAIL_BOTTOM_Y,
  topY: EL_DECK_RAIL_TOP_Y,
};

// --- E-3: platform content. Benches/lamp/trash-can reuse the SAME shipped
// props (prp_bench/prp_lamp/prp_trash_can) already live elsewhere on the
// block — colliders below translate the SAME measured per-unit box
// STREET_PROP_COLLIDERS/PARK_PROPS already established for each prop type
// (bench: PARK_PROPS' own width 2.2/depth 0.6/height 0.45; lamp/trash-can:
// STREET_PROP_COLLIDERS' own measured ±0.63/±0.3/h3.62 and ±0.51/±0.5/h1.31
// respectively — unrotated placement, so world = local + position, same
// technique POOLHOUSE_PIER_COLLIDERS/LIFEGUARD_CHAIR_COLLIDER use), just
// re-based onto the deck's own walking height (EL_DECK_WALK_Y) instead of
// street level 0. Placed along the platform's two rail edges at z=±1.3 —
// comfortably inside the rail-clamped ±1.7 reachable band with real margin
// on both the rail side and the open-walkway side — spread across x[6,24],
// clear of the train/nose corridor (x<3), the station stair's own x-range
// (26.5-29.5), and the bumper/vantage/collectible cluster further east.
export const EL_STATION_BENCH_POS = [
  { x: 6, z: -1.3 },
  { x: 20, z: 1.3 },
];
export const EL_STATION_BENCH_COLLIDERS: BoxCollider[] = EL_STATION_BENCH_POS.map((p) => ({
  minX: p.x - 1.1,
  maxX: p.x + 1.1,
  minZ: p.z - 0.3,
  maxZ: p.z + 0.3,
  bottomY: EL_DECK_WALK_Y,
  topY: EL_DECK_WALK_Y + 0.45,
}));

export const EL_STATION_LAMP_POS = [
  { x: 10, z: 1.3 },
  { x: 24, z: -1.3 },
];
export const EL_STATION_LAMP_COLLIDERS: BoxCollider[] = EL_STATION_LAMP_POS.map((p) => ({
  minX: p.x - 0.63,
  maxX: p.x + 0.63,
  minZ: p.z - 0.3,
  maxZ: p.z + 0.3,
  bottomY: EL_DECK_WALK_Y,
  topY: EL_DECK_WALK_Y + 3.62,
}));

export const EL_STATION_TRASH_CAN_POS = { x: 16, z: -1.3 };
export const EL_STATION_TRASH_CAN_COLLIDER: BoxCollider = {
  minX: EL_STATION_TRASH_CAN_POS.x - 0.51,
  maxX: EL_STATION_TRASH_CAN_POS.x + 0.51,
  minZ: EL_STATION_TRASH_CAN_POS.z - 0.5,
  maxZ: EL_STATION_TRASH_CAN_POS.z + 0.5,
  bottomY: EL_DECK_WALK_Y,
  topY: EL_DECK_WALK_Y + 1.31,
};

// A checkpoint at the station end (near the top of the station stair,
// x-centered on its own 26.5-29.5 span) — world/checkpoints.ts's own
// 'el_station' entry reads this. `requireY` (mirrored from
// world/interact.ts's own established pattern, see EL_STATION_VANTAGE_MARKER
// below) is REQUIRED here, not optional: this XZ sits directly above the
// open main street/intersection at street level, which `CheckpointTracker.
// update()`'s XZ-only trigger would otherwise also satisfy for a street-level
// pedestrian who never climbed the stair.
export const EL_STATION_CHECKPOINT_MARKER = { x: 28, z: 0, y: EL_DECK_WALK_Y };

// A vantage-style interactable mirroring ROOFTOP_VANTAGE_MARKER's own shape
// exactly (world/interact.ts's own established "requireY excludes ground
// level" pattern) — same street-overlap reasoning as the checkpoint above,
// made sharper here since `corona_exit_seam` (world/interact.ts, x=32 z=0
// radius 2.4) already occupies this XZ neighborhood at STREET level; without
// `requireY`, a ground-level player near the Corona gate could see this
// deck-only prompt too. Placed near the platform's own east end, offering a
// "look out over Corona" beat that ties into E-1's own Corona-continuation
// dressing.
export const EL_STATION_VANTAGE_MARKER = { x: 29, z: -1.3, y: EL_DECK_WALK_Y };

// I-4's exported persistent-collectible primitive (loot/pickup.ts's
// `spawnPersistentCollectible`) — one placed here per E-3's own named
// deliverable, id 'station_platform' (matching loot/droptable.ts's own
// illustrative example for this exact id). main.ts spawns it once at boot
// from this position. y is the walk surface itself — `PickupPool.spawn()`
// already adds its own +0.3 lift onto whatever origin.y it's given (see its
// own `p.position.set(..., origin.y + 0.3, ...)`), matching every other
// spawn call in the game; pre-adding it here a second time would have lifted
// the pickup mesh to 0.6m above the deck instead of the intended 0.3m.
export const EL_STATION_COLLECTIBLE_POS = { x: 29, z: 1.3, y: EL_DECK_WALK_Y };

// --- B-3: Astoria Park corner (poolhouse + lifeguard chair + flagpole) ---
// Placed along the park pocket's own north edge, clear of every existing
// PARK_PROPS instance (checked by hand: nearest is bench_1 at (-20,22),
// >2m real margin from the poolhouse's own footprint).
export const POOLHOUSE_POS = { x: -20, z: 25 };
/** Measured live off `blk_park_poolhouse.glb`: 3 `COL_box_pool_pier` nodes at local (∓3.7/0, 2.2, -0.05), half-extents (0.3, 2.2, 0.35) — unrotated placement, so world = local + position. */
export const POOLHOUSE_PIER_COLLIDERS: BoxCollider[] = [-3.7, 0, 3.7].map((lx) => ({
  minX: POOLHOUSE_POS.x + lx - 0.3,
  maxX: POOLHOUSE_POS.x + lx + 0.3,
  minZ: POOLHOUSE_POS.z - 0.05 - 0.35,
  maxZ: POOLHOUSE_POS.z - 0.05 + 0.35,
  bottomY: 0,
  topY: 4.4,
}));
export const LIFEGUARD_CHAIR_POS = { x: -14, z: 22 };
// REVIEW FIX (MEDIUM, correctness lens): topY was 2×translation.y (1.536)
// instead of translation.y+half-extent (0.768+0.75=1.518) — same slip as
// FAIR_STALL_COLLIDERS' counter / LONGTABLE_COLLIDER, fixed the same way.
export const LIFEGUARD_CHAIR_COLLIDER: BoxCollider = { minX: -14.5, maxX: -13.5, minZ: 21.65, maxZ: 22.35, bottomY: 0.018, topY: 1.518 };
export const FLAGPOLE_POS = { x: -24, z: 16 };
export const FLAGPOLE_COLLIDER: BoxCollider = { minX: -24.1, maxX: -23.9, minZ: 15.9, maxZ: 16.1, bottomY: 0, topY: 6 };

// --- B-3: Beer garden corner ---
// Placed south of building D (x8-26, z-26..-8), clear of CONSTRUCTION_POCKET
// (x-13..13) with real margin. rotationY=π so the gate opening (the front
// wall's own local -Z face) turns to face NORTH, toward the block interior,
// rather than the boundary — a player approaches from the street, not from
// the edge of the map.
export const BEER_GARDEN_POS = { x: 20, z: -32 };
/** Measured live off `blk_beergarden_wall.glb`, transformed through rotationY=π (local(x,z) -> world(-x,-z) relative to position, half-extents unchanged). */
export const BEER_GARDEN_COLLIDERS: BoxCollider[] = [
  { minX: 21.6, maxX: 24.0, minZ: -29.15, maxZ: -28.85, bottomY: 0, topY: 2.5 }, // front_l
  { minX: 16.0, maxX: 18.4, minZ: -29.15, maxZ: -28.85, bottomY: 0, topY: 2.5 }, // front_r — 3.2m gate opening between this and front_l
  { minX: 23.85, maxX: 24.15, minZ: -35, maxZ: -29, bottomY: 0, topY: 2.5 }, // side run
];
export const LONGTABLE_POS = { x: 20, z: -31 };
// REVIEW FIX (MEDIUM, correctness lens): topY was 2×translation.y (1.008)
// instead of translation.y+half-extent (0.5041+0.45=0.9541) — same slip as
// FAIR_STALL_COLLIDERS' counter above, fixed the same way.
export const LONGTABLE_COLLIDER: BoxCollider = { minX: 18.25, maxX: 21.75, minZ: -32.15, maxZ: -29.85, bottomY: 0.0541, topY: 0.9541 };
export const STRING_LIGHTS_POS = { x: 19, z: -31.5 };
/**
 * REVIEW FIX (LOW, spec-fidelity lens): the brief's item 11 explicitly
 * requires "COL boxes on posts only" — the first version of this file
 * shipped `prp_string_lights` with no collider at all, inconsistent with
 * this same file's own §0.4 reasoning for EL_DECK_RAILS above (visible
 * geometry needs matching collision). Measured live off
 * prp_string_lights.glb: two `COL_box_post` nodes at local (∓4, 1.8, 0),
 * half-extent (0.1, 1.8, 0.1) — unrotated placement, so world = local +
 * position. East post (world x≈23) clears the beer garden's own side wall
 * (x 23.85-24.15) by 0.75m real margin.
 */
export const STRING_LIGHTS_COLLIDERS: BoxCollider[] = [-4, 4].map((lx) => ({
  minX: STRING_LIGHTS_POS.x + lx - 0.1,
  maxX: STRING_LIGHTS_POS.x + lx + 0.1,
  minZ: STRING_LIGHTS_POS.z - 0.1,
  maxZ: STRING_LIGHTS_POS.z + 0.1,
  bottomY: 0,
  topY: 3.6,
}));

// --- B-3: Restaurant row (taverna + seafood) ---
// A NEW standalone strip east of building B (x19-26) — every existing
// building's own C04 facade already wraps its FULL footprint (attachArt's
// per-building GLB carries all 4 walls + roof, not just the street-facing
// side), so there's no bare existing wall to mount a NEW storefront onto;
// these stand alone the same way blk_beergarden_wall/blk_park_poolhouse do.
// rotationY=π/2 turns each facade's own local -Z (front) to face world -X
// (west, toward building B and the sidewalk a player actually walks).
export const TAVERNA_POS = { x: 29, z: 15 };
export const SEAFOOD_POS = { x: 29, z: 21 };
/** Measured live off blk_storefront_taverna.glb / blk_storefront_seafood.glb (both share the same awning-post local geometry), transformed through rotationY=π/2 (local(x,z) -> world(z,-x) relative to position). */
export const TAVERNA_COLLIDERS: BoxCollider[] = [
  { minX: 28.31, maxX: 28.45, minZ: 17.28, maxZ: 17.42, bottomY: 0, topY: 3.0 }, // awning post
  { minX: 28.31, maxX: 28.45, minZ: 12.58, maxZ: 12.72, bottomY: 0, topY: 3.0 }, // awning post
  { minX: 28.24, maxX: 28.36, minZ: 12.325, maxZ: 12.975, bottomY: 0.25, topY: 1.55 }, // menu board
];
export const SEAFOOD_COLLIDERS: BoxCollider[] = [
  { minX: 28.31, maxX: 28.45, minZ: 23.28, maxZ: 23.42, bottomY: 0, topY: 3.0 }, // awning post
  { minX: 28.31, maxX: 28.45, minZ: 18.58, maxZ: 18.72, bottomY: 0, topY: 3.0 }, // awning post
  { minX: 28.1, maxX: 28.8, minZ: 18.6, maxZ: 21.0, bottomY: 0.325, topY: 1.075 }, // tilted ice display
];
/**
 * Wave P-4 (PLAN-CITY3 §3, L4 corollary — CITY3-FINDINGS.md lane-4 finding
 * #11): the two storefronts' own solid `facade` wall mesh (6.0m x 4.0m, both
 * GLBs measured live) had NO matching collider at all — only the awning
 * posts/menu-board/ice-display above did — so the player walked straight
 * through both walls into the empty east strip behind them. Coordinates are
 * the wall's own measured world bbox after rotationY=PI/2 (TAVERNA_COLLIDERS'
 * own comment has the transform). Deliberately contiguous in Z (both meet at
 * z=18) — one unbroken 12m frontage, matching the row's own read as a single
 * strip, not two independently-sized ones.
 */
export const TAVERNA_FACADE_COLLIDER: BoxCollider = { minX: 28.68, maxX: 29.0, minZ: 12, maxZ: 18, bottomY: 0, topY: 4.0 };
export const SEAFOOD_FACADE_COLLIDER: BoxCollider = { minX: 28.68, maxX: 29.0, minZ: 18, maxZ: 24, bottomY: 0, topY: 4.0 };
export const FISH_CRATE_POS = { x: 28, z: 22 };
export const FISH_CRATE_COLLIDER: BoxCollider = { minX: 27.55, maxX: 28.45, minZ: 21.65, maxZ: 22.35, bottomY: 0, topY: 0.86 };
export const FISH_DISPLAY_POS = { x: 27, z: 23.5 };
export const FISH_DISPLAY_COLLIDER: BoxCollider = { minX: 26.1, maxX: 27.9, minZ: 23.085, maxZ: 23.915, bottomY: 0, topY: 0.915 };

// --- B-3: Street fair (fair stall + grill cart) ---
export const FAIR_STALL_POS = { x: -9, z: 10 };
/**
 * Measured live off prp_fair_stall.glb: COL_box_counter (open front, blocks
 * the counter only) + COL_box_rearwall — the canopy roof stays non-
 * collidable per the brief. Unrotated placement.
 * REVIEW FIX (MEDIUM, correctness lens): the counter's topY was computed as
 * 2×translation.y (1.5) instead of translation.y+half-extent (0.75+0.25=1.0)
 * — the same slip as LONGTABLE_COLLIDER/LIFEGUARD_CHAIR_COLLIDER below, all
 * three fixed together. At the WRONG 1.5 the counter exceeded
 * VAULT_MAX_HEIGHT (1.1, controller.ts), making a real-world ~1m counter
 * unvaultable; at the correct 1.0 it's properly vaultable.
 */
export const FAIR_STALL_COLLIDERS: BoxCollider[] = [
  { minX: -10.5, maxX: -7.5, minZ: 9.625, maxZ: 10.375, bottomY: 0.5, topY: 1.0 }, // counter
  { minX: -10.5, maxX: -7.5, minZ: 10.39, maxZ: 10.51, bottomY: 0.8, topY: 2.4 }, // rear wall
];
export const GRILL_CART_POS = { x: -22, z: 10 };
export const GRILL_CART_COLLIDER: BoxCollider = { minX: -22.85, maxX: -21.15, minZ: 9.6, maxZ: 10.4, bottomY: 0, topY: 1.3 };

// --- B-3: skyline vista (RFK bridge — background, no collision) ---
// Placed well beyond the ±38 boundary (z=100, matching blk_hellgate_
// backdrop's own z=110 "distant, monumental" precedent) and offset west of
// Hell Gate (x=0) so the two landmarks read as distinct, not overlapping.
// One span tile (24.0m, matching blk_rfk_span's own authored length) fits
// exactly between two towers placed 24m apart, per the brief's own
// "tiles between blk_rfk_tower instances placed on a 24m spacing" note.
export const RFK_TOWER_XS = [-45, -21];
export const RFK_SPAN_CENTER_X = -33;
export const RFK_VISTA_Z = 100;
export const RFK_TOWER_PLACEMENT_Y = 0;
export const RFK_SPAN_PLACEMENT_Y = 12; // matches the delivered GLB's own recorded placementY

// --- B-3: THE BOMB (hero pickup, replaces the bodega's gray heal marker) + its hanging sign (exterior, above the bodega door) ---
export const BOMB_SIGN_POS = { x: BODEGA_DOOR.x, y: 2.4, z: BODEGA_DOOR.z - 0.15 }; // matches the door's own listed height; z nudged 0.15m toward the street so it protrudes from the wall like a real hanging sign
